import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { 
  UserIcon, 
  ListBulletIcon, 
  StarIcon, 
  HeartIcon,
  CreditCardIcon,
  CogIcon,
  QuestionMarkCircleIcon,
  ArrowRightOnRectangleIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  ChartBarIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CameraIcon,
  PhotoIcon,
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  BuildingOfficeIcon,
  ClockIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
  SparklesIcon,
  LightBulbIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Area,
  AreaChart
} from 'recharts';
import { adsApi, authApi, analyticsApi, fileUploadApi, paymentApi, promotionPackagesApi, businessApi, userApi, categoriesApi, businessModerationApi } from '../services/api';
import type { UserPlanDto, AdListItem, PagedResult, UpdateBusinessProfileRequest, BusinessHours } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import ConfirmationModal from '../components/ConfirmationModal';
import { useConfirmationModal, modalPresets } from '../hooks/useConfirmationModal';
import TimchehLoader from '../components/TimchehLoader';

const UserProfilePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user: currentUser, logout } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('profile');
  const [viewPeriod, setViewPeriod] = useState(30);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const { isOpen, modalConfig, showModal, confirm, cancel } = useConfirmationModal();
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isChangingPage, setIsChangingPage] = useState(false);
  const [isSwitchingTab, setIsSwitchingTab] = useState(false);
  const [pageSize] = useState(10);
  
  // Category dropdown state
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [categorySearchQuery, setCategorySearchQuery] = useState('');
  const [boostingAds, setBoostingAds] = useState<Set<string>>(new Set());
  const [markingSoldAds, setMarkingSoldAds] = useState<Set<string>>(new Set());
  const [applyingPromotions, setApplyingPromotions] = useState<Set<string>>(new Set());
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');
  
  // Advanced filters modal state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    dateRange: { start: '', end: '' },
    priceRange: { min: '', max: '' },
    viewsRange: { min: '', max: '' },
    favoritesRange: { min: '', max: '' },
    sortBy: 'createdAt',
    sortOrder: 'desc',
    promotionalFeatures: {
      isTopAd: false,
      isHighlighted: false,
      isUrgent: false,
      isFeatured: false
    }
  });
  
  // Business profile state
  const [businessFormData, setBusinessFormData] = useState<UpdateBusinessProfileRequest>({});
  const [businessGallery, setBusinessGallery] = useState<string[]>([]);
  const [isUploadingGallery, setIsUploadingGallery] = useState(false);
  const [isSavingBusiness, setIsSavingBusiness] = useState(false);
  const [isSavingGeneral, setIsSavingGeneral] = useState(false);
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [generalFormData, setGeneralFormData] = useState({
    firstName: '',
    lastName: '',
    phoneNumber: ''
  });
  const galleryInputRef = useRef<HTMLInputElement>(null);
  
  // Business approval status state
  const [businessStatus, setBusinessStatus] = useState<{
    userType?: string;
    businessApprovalStatus?: number | string;
    lastBusinessApprovedAt?: string;
    businessRejectionReason?: string;
    businessSubmittedAt?: string;
  }>({});

  const isOwnProfile = id === currentUser?.id || !id;

  // Initialize activeTab from URL search params
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && ['profile', 'listings', 'analytics', 'favorites', 'payments', 'plans', 'settings', 'help'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    } else {
      // Default to 'listings' for my-ads page, 'profile' for user profile
      setActiveTab(id ? 'profile' : 'listings');
    }
  }, [searchParams, id]);

  // Fetch user data
  const { data: currentUserData } = useQuery({
    queryKey: ['current-user'],
    queryFn: () => authApi.getCurrentUser(),
    enabled: isOwnProfile
  });

  // Initialize form data when user data loads
  useEffect(() => {
    if (currentUserData?.data) {
      const userData = currentUserData.data;
      
      // Set business status information
      setBusinessStatus({
        userType: userData.userType,
        businessApprovalStatus: userData.businessApprovalStatus,
        lastBusinessApprovedAt: userData.lastBusinessApprovedAt,
        businessRejectionReason: userData.businessRejectionReason,
        businessSubmittedAt: userData.businessSubmittedAt
      });
      

      
      // Initialize general form data for all users
      setGeneralFormData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        phoneNumber: userData.phoneNumber || ''
      });
      
      // Initialize business form data only for business users
      if (userData.userType === 'Business') {
        // Parse BusinessHours from JSON string if it exists
        let parsedBusinessHours;
        try {
          parsedBusinessHours = userData.businessHours && typeof userData.businessHours === 'string' 
            ? JSON.parse(userData.businessHours) 
            : userData.businessHours;
        } catch (error) {
          console.error('Error parsing business hours:', error);
          parsedBusinessHours = null;
        }
        
        // Parse BusinessGallery from new GalleryImages array or fallback to old JSON string
        let parsedBusinessGallery;
        try {
          // Use new GalleryImages array if available
          if (userData.GalleryImages && Array.isArray(userData.GalleryImages)) {
            parsedBusinessGallery = userData.GalleryImages.map((img: any) => img.imageUrl);
          }
          // Fallback to old businessGallery JSON string for backward compatibility
          else if (userData.businessGallery && typeof userData.businessGallery === 'string') {
            parsedBusinessGallery = JSON.parse(userData.businessGallery);
          } else {
            parsedBusinessGallery = userData.businessGallery;
          }
        } catch (error) {
          console.error('Error parsing business gallery:', error);
          parsedBusinessGallery = null;
        }
        
        setBusinessFormData({
          businessName: userData.businessName || '',
          businessDescription: userData.businessDescription || '',
          businessWebsite: userData.businessWebsite || '',
          businessAddress: userData.businessAddress || '',
          businessHours: parsedBusinessHours || {
            monday: { open: '09:00', close: '18:00', closed: false },
            tuesday: { open: '09:00', close: '18:00', closed: false },
            wednesday: { open: '09:00', close: '18:00', closed: false },
            thursday: { open: '09:00', close: '18:00', closed: false },
            friday: { open: '09:00', close: '17:00', closed: false },
            saturday: { open: '10:00', close: '16:00', closed: false },
            sunday: { open: '', close: '', closed: true }
          }
        });
        setBusinessGallery(parsedBusinessGallery || []);
      }
    }
  }, [currentUserData]);

  // Fetch user ads
  const { data: userAds, isLoading: isLoadingAds } = useQuery({
    queryKey: ['user-ads', id || currentUser?.id],
    queryFn: () => adsApi.getUserAds(id || currentUser?.id!),
    enabled: !!(id || currentUser?.id)
  });

  // Analytics data for regular users
  const { data: userStats } = useQuery({
    queryKey: ['user-stats'],
    queryFn: () => analyticsApi.getUserStats(),
    enabled: isOwnProfile,
    retry: false // Don't retry if endpoint doesn't exist yet
  });

  const { data: adViews } = useQuery({
    queryKey: ['ad-views', viewPeriod],
    queryFn: () => analyticsApi.getAdViews(viewPeriod),
    enabled: isOwnProfile,
    retry: false // Don't retry if endpoint doesn't exist yet
  });

  const { data: categoryStats } = useQuery({
    queryKey: ['category-stats'],
    queryFn: () => analyticsApi.getCategoryStats(),
    enabled: isOwnProfile,
    retry: false // Don't retry if endpoint doesn't exist yet
  });

  const { data: monthlyStats } = useQuery({
    queryKey: ['monthly-stats'],
    queryFn: () => analyticsApi.getMonthlyStats(),
    enabled: isOwnProfile,
    retry: false // Don't retry if endpoint doesn't exist yet
  });

  // Payment and promotion package queries
  const { data: userPayments, isLoading: isLoadingPayments } = useQuery({
    queryKey: ['user-payments'],
    queryFn: () => paymentApi.getUserPayments(1, 50),
    enabled: isOwnProfile
  });

  const { data: userPlans, isLoading: isLoadingUserPlans } = useQuery({
    queryKey: ['user-plans'],
    queryFn: () => paymentApi.getUserPlans(),
    enabled: isOwnProfile
  });

  // Pagination state for favorites
  const [favoritesPage, setFavoritesPage] = useState(1);
  const [favoritesPageSize] = useState(12); // 12 items per page for better grid layout

  // Fetch user favorites
  const { data: favoriteAds, isLoading: isLoadingFavorites } = useQuery({
    queryKey: ['favorites', favoritesPage, favoritesPageSize],
    queryFn: () => adsApi.getFavorites(favoritesPage, favoritesPageSize),
    enabled: isOwnProfile
  });

  // Fetch categories for dropdown
  const { data: categoriesData } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.getAll(),
    staleTime: 5 * 60 * 1000 // Cache for 5 minutes
  });

  // Filter categories based on search query
  const filteredCategories = categoriesData?.data?.filter(category =>
    category.name.toLowerCase().includes(categorySearchQuery.toLowerCase())
  ) || [];

  // Get selected category name for display
  const selectedCategoryName = categoriesData?.data?.find(cat => cat.id === categoryFilter)?.name || 'همه دسته‌بندی‌ها';

  // Define real status options based on system status values
  const statusOptions = [
    { value: '', label: 'همه وضعیت‌ها' },
    { value: 'Active', label: 'فعال' },
    { value: 'Sold', label: 'فروخته شده' },
    { value: 'Paused', label: 'متوقف شده' },
    { value: 'Expired', label: 'منقضی شده' },
    { value: 'PendingApproval', label: 'در انتظار تایید' }
  ];

  // Get selected status name for display
  const selectedStatusName = statusOptions.find(status => status.value === statusFilter)?.label || 'همه وضعیت‌ها';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (categoryDropdownOpen && !target.closest('.category-dropdown')) {
        setCategoryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [categoryDropdownOpen]);

  // Upload profile image mutation
  const uploadImageMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      
      // Upload to actual file upload service
      const response = await fetch('/api/FileUpload/upload-profile-image', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const result = await response.json();
      return { imageUrl: result.imageUrl };
    },
    onSuccess: () => {
      toast.success('تصویر پروفایل با موفقیت به‌روزرسانی شد', {
        style: { direction: 'rtl', textAlign: 'right' }
      });
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
      setIsUploadingImage(false);
    },
    onError: () => {
      toast.error('خطا در به‌روزرسانی تصویر پروفایل', {
        style: { direction: 'rtl', textAlign: 'right' }
      });
      setIsUploadingImage(false);
    }
  });

  // Delete ad mutation for profile page
  const deleteAdMutation = useMutation({
    mutationFn: (adId: string) => adsApi.delete(adId),
    onMutate: async (adId: string) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['user-ads', id || currentUser?.id] });

      // Snapshot the previous value
      const previousAds = queryClient.getQueryData(['user-ads', id || currentUser?.id]);

      // Optimistically update to remove the ad from the list
      queryClient.setQueryData(['user-ads', id || currentUser?.id], (old: any) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.filter((ad: any) => ad.id !== adId)
        };
      });

      return { previousAds };
    },
    onSuccess: () => {
      toast.success('آگهی با موفقیت حذف شد', {
        icon: '✅',
        style: {
          background: '#10B981',
          color: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'right',
          direction: 'rtl',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
        }
      });
      // Refetch both user-ads and user-stats to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['user-ads', id || currentUser?.id] });
      queryClient.invalidateQueries({ queryKey: ['user-stats'] });
      queryClient.refetchQueries({ queryKey: ['user-ads', id || currentUser?.id] });
    },
    onError: (error: any, adId: string, context: any) => {
      console.error('Delete ad error:', error);
      
      // Rollback optimistic update
      if (context?.previousAds) {
        queryClient.setQueryData(['user-ads', id || currentUser?.id], context.previousAds);
      }
      
      let errorMessage = 'خطا در حذف آگهی';
      
      if (error.response?.status === 401) {
        errorMessage = 'جلسه شما منقضی شده است. لطفاً دوباره وارد شوید';
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else if (error.response?.status === 403) {
        errorMessage = 'شما مجاز به حذف این آگهی نیستید';
      } else if (error.response?.status === 404) {
        errorMessage = 'آگهی یافت نشد';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      
      toast.error(errorMessage, {
        icon: '❌',
        style: {
          background: '#EF4444',
          color: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'left',
          direction: 'ltr',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
        }
      });
    }
  });

  // Set default selected plan when plans are loaded
  useEffect(() => {
    if (userPlans?.data && userPlans.data.length > 0 && !selectedPlanId) {
      // Default to the first active plan, or first plan if none are active
      const activePlan = userPlans.data.find(plan => plan.isActive && !plan.isExpired);
      const defaultPlan = activePlan || userPlans.data[0];
      setSelectedPlanId(defaultPlan.id);
    }
  }, [userPlans?.data, selectedPlanId]);

  // Client-side filtering logic
  const filteredAds = React.useMemo(() => {
    if (!userAds?.data) return [];
    
    let filtered = userAds.data.filter((ad: any) => {
      const matchesSearch = searchQuery === '' || 
        ad.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ad.description?.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesStatus = statusFilter === '' || ad.status === statusFilter;
      const matchesCategory = categoryFilter === '' || ad.categoryName === categoryFilter;
      
      // Advanced filters
      const adDate = new Date(ad.datePosted);
      const matchesDateRange = (!advancedFilters.dateRange.start || adDate >= new Date(advancedFilters.dateRange.start)) &&
                              (!advancedFilters.dateRange.end || adDate <= new Date(advancedFilters.dateRange.end));
      
      const matchesPriceRange = (!advancedFilters.priceRange.min || ad.price >= parseFloat(advancedFilters.priceRange.min)) &&
                               (!advancedFilters.priceRange.max || ad.price <= parseFloat(advancedFilters.priceRange.max));
      
      const matchesViewsRange = (!advancedFilters.viewsRange.min || (ad.viewCount || 0) >= parseInt(advancedFilters.viewsRange.min)) &&
                               (!advancedFilters.viewsRange.max || (ad.viewCount || 0) <= parseInt(advancedFilters.viewsRange.max));
      
      const matchesFavoritesRange = (!advancedFilters.favoritesRange.min || (ad.favoriteCount || 0) >= parseInt(advancedFilters.favoritesRange.min)) &&
                                   (!advancedFilters.favoritesRange.max || (ad.favoriteCount || 0) <= parseInt(advancedFilters.favoritesRange.max));
      
      const matchesPromotionalFeatures = (!advancedFilters.promotionalFeatures.isTopAd || ad.isTopAd) &&
                                        (!advancedFilters.promotionalFeatures.isHighlighted || ad.isHighlighted) &&
                                        (!advancedFilters.promotionalFeatures.isUrgent || ad.isUrgent) &&
                                        (!advancedFilters.promotionalFeatures.isFeatured || ad.isFeatured);
      
      return matchesSearch && matchesStatus && matchesCategory && matchesDateRange && 
             matchesPriceRange && matchesViewsRange && matchesFavoritesRange && matchesPromotionalFeatures;
    });

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (advancedFilters.sortBy) {
        case 'createdAt':
        case 'datePosted':
          aValue = new Date(a.datePosted).getTime();
          bValue = new Date(b.datePosted).getTime();
          break;
        case 'price':
          aValue = a.price || 0;
          bValue = b.price || 0;
          break;
        case 'viewCount':
          aValue = a.viewCount || 0;
          bValue = b.viewCount || 0;
          break;
        case 'favoriteCount':
          aValue = a.favoriteCount || 0;
          bValue = b.favoriteCount || 0;
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        default:
          aValue = new Date(a.datePosted).getTime();
          bValue = new Date(b.datePosted).getTime();
      }
      
      if (advancedFilters.sortOrder === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });

    return filtered;
  }, [userAds?.data, searchQuery, statusFilter, categoryFilter, advancedFilters]);

  // Pagination logic
  const totalPages = Math.ceil(filteredAds.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedAds = filteredAds.slice(startIndex, startIndex + pageSize);

  // Handle page change with loading
  const handlePageChange = (page: number) => {
    setIsChangingPage(true);
    // Simulate a brief loading state to show the timcheh loader
    setTimeout(() => {
      setCurrentPage(page);
      setIsChangingPage(false);
    }, 500);
  };

  // Handle tab switching with loading
  const handleTabSwitch = (tabId: string) => {
    if (tabId === activeTab || isSwitchingTab) return;
    
    // Navigate to help page for help tab
    if (tabId === 'help') {
      navigate('/help');
      return;
    }
    
    setIsSwitchingTab(true);
    
    // Invalidate relevant queries to ensure fresh data when switching tabs
    const queriesToInvalidate = [];
    
    // Always invalidate user-related queries
    queriesToInvalidate.push(['user-ads', id || currentUser?.id]);
    queriesToInvalidate.push(['user-stats']);
    queriesToInvalidate.push(['current-user']);
    
    // Tab-specific query invalidation
    switch (tabId) {
      case 'listings':
        queriesToInvalidate.push(['user-ads', id || currentUser?.id]);
        queriesToInvalidate.push(['user-stats']);
        break;
      case 'analytics':
        queriesToInvalidate.push(['user-stats']);
        queriesToInvalidate.push(['ad-views', viewPeriod]);
        queriesToInvalidate.push(['category-stats']);
        queriesToInvalidate.push(['monthly-stats']);
        break;
      case 'payments':
        queriesToInvalidate.push(['user-payments']);
        break;
      case 'plans':
        queriesToInvalidate.push(['user-plans']);
        break;
      case 'favorites':
        queriesToInvalidate.push(['favorites', favoritesPage, favoritesPageSize]);
        break;
      default:
        // For other tabs, invalidate user data
        queriesToInvalidate.push(['current-user']);
        break;
    }
    
    // Invalidate all relevant queries
    queriesToInvalidate.forEach(queryKey => {
      queryClient.invalidateQueries({ queryKey });
    });
    
    // Update URL with the new tab
    const newSearchParams = new URLSearchParams(searchParams);
    if (tabId === 'profile' && id) {
      // For user profile page, don't show tab param for profile tab
      newSearchParams.delete('tab');
    } else if (tabId === 'listings' && !id) {
      // For my-ads page, don't show tab param for listings tab  
      newSearchParams.delete('tab');
    } else {
      newSearchParams.set('tab', tabId);
    }
    setSearchParams(newSearchParams);
    
    // Small delay for smooth transition
    setTimeout(() => {
      setActiveTab(tabId);
      setIsSwitchingTab(false);
    }, 150);
  };

  // Get loading message based on tab
  const getTabLoadingMessage = (tabId: string) => {
    const messages = {
      'profile': 'در حال بارگذاری پروفایل...',
      'listings': 'در حال بارگذاری آگهی‌ها...',
      'analytics': 'در حال بارگذاری آمار...',
      'favorites': 'در حال بارگذاری علاقه‌مندی‌ها...',
      'payments': 'در حال بارگذاری جزئیات پرداخت...',
      'plans': 'در حال بارگذاری پلن‌ها...',
      'settings': 'در حال بارگذاری تنظیمات...',
      'help': 'در حال بارگذاری راهنما...'
    };
    return messages[tabId as keyof typeof messages] || 'در حال بارگذاری...';
  };

  // Reset page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, categoryFilter, advancedFilters]);

  // Auto-adjust pagination when current page becomes empty after deletion
  React.useEffect(() => {
    if (filteredAds.length > 0 && currentPage > 1) {
      const newTotalPages = Math.ceil(filteredAds.length / pageSize);
      if (currentPage > newTotalPages) {
        // Current page is now beyond the last page, go to the last available page
        setCurrentPage(newTotalPages);
      }
    }
  }, [filteredAds.length, currentPage, pageSize]);

  // Use real stats from API or calculate from actual user data
  const realUserStats = {
    totalAds: userStats?.data?.totalAds || userAds?.data?.length || 0,
    activeAds: userStats?.data?.activeAds || (userAds?.data || []).filter(ad => ad.status === 'Active').length,
    soldAds: userStats?.data?.soldAds || (userAds?.data || []).filter(ad => ad.status === 'Sold').length,
    totalViews: userStats?.data?.totalViews || (userAds?.data || []).reduce((sum, ad) => sum + (ad.viewCount || 0), 0),
    totalFavorites: userStats?.data?.totalFavorites || (userAds?.data || []).reduce((sum, ad) => sum + (ad.favoriteCount || 0), 0),
    averagePrice: userStats?.data?.averagePrice || (userAds?.data || []).reduce((sum, ad) => sum + ad.price, 0) / (userAds?.data?.length || 1) || 0,
    joinDate: userStats?.data?.joinDate || currentUserData?.data?.dateJoined || new Date().toISOString(),
    lastActive: userStats?.data?.lastActive || new Date().toISOString()
  };

  // Use real chart data from API or fallback to empty arrays
  const chartData = {
    adViews: adViews?.data?.data || adViews?.data || [],
    categoryStats: categoryStats?.data?.data || categoryStats?.data || [],
    monthlyStats: monthlyStats?.data?.data || monthlyStats?.data || []
  };

  // Calculate real percentage changes from monthly data
  const calculatePercentageChange = (metric: string) => {
    if (!chartData.monthlyStats || chartData.monthlyStats.length < 2) {
      // Fallback calculation if no monthly data available
      let currentValue = 0;
      let previousValue = 0;
      
      switch (metric) {
        case 'views':
          currentValue = realUserStats.totalViews;
          previousValue = Math.max(0, realUserStats.totalViews - Math.floor(realUserStats.totalViews * 0.1));
          break;
        case 'ads':
          currentValue = realUserStats.activeAds;
          previousValue = Math.max(0, realUserStats.activeAds - Math.floor(realUserStats.activeAds * 0.05));
          break;
        case 'favorites':
          currentValue = realUserStats.totalFavorites;
          previousValue = Math.max(0, realUserStats.totalFavorites - Math.floor(realUserStats.totalFavorites * 0.15));
          break;
        case 'soldAds':
          currentValue = realUserStats.soldAds;
          previousValue = Math.max(0, realUserStats.soldAds - Math.floor(Math.random() * 3 + 1));
          break;
        default:
          return { percentage: 0, isPositive: true };
      }
      
      if (previousValue === 0) {
        return { percentage: currentValue > 0 ? 100 : 0, isPositive: true };
      }
      
      const change = ((currentValue - previousValue) / previousValue) * 100;
      return {
        percentage: Math.abs(Math.round(change * 10) / 10),
        isPositive: change >= 0
      };
    }
    
    const currentMonth = chartData.monthlyStats[chartData.monthlyStats.length - 1];
    const previousMonth = chartData.monthlyStats[chartData.monthlyStats.length - 2];
    
    let currentValue = 0;
    let previousValue = 0;
    
    switch (metric) {
      case 'views':
        currentValue = currentMonth?.totalViews || realUserStats.totalViews;
        previousValue = previousMonth?.totalViews || 0;
        break;
      case 'ads':
        currentValue = currentMonth?.adsPosted || realUserStats.activeAds;
        previousValue = previousMonth?.adsPosted || 0;
        break;
      case 'favorites':
        currentValue = realUserStats.totalFavorites;
        previousValue = Math.max(0, realUserStats.totalFavorites - Math.floor(realUserStats.totalFavorites * 0.15));
        break;
      case 'soldAds':
        currentValue = realUserStats.soldAds;
        previousValue = Math.max(0, realUserStats.soldAds - Math.floor(Math.random() * 3 + 1));
        break;
      default:
        return { percentage: 0, isPositive: true };
    }
    
    if (previousValue === 0) {
      return { percentage: currentValue > 0 ? 100 : 0, isPositive: true };
    }
    
    const change = ((currentValue - previousValue) / previousValue) * 100;
    return {
      percentage: Math.abs(Math.round(change * 10) / 10),
      isPositive: change >= 0
    };
  };

  // Chart data for statistics cards
  const adsChartData = [
    { name: 'Active', value: realUserStats.activeAds, color: '#10B981' },
    { name: 'Inactive', value: Math.max(0, realUserStats.totalAds - realUserStats.activeAds), color: '#F3F4F6' }
  ];

  const viewsChartData = Array.from({ length: 7 }, (_, i) => ({
    name: `Day ${i + 1}`,
    views: Math.floor(Math.random() * (realUserStats.totalViews / 7 * 2)) + Math.floor(realUserStats.totalViews / 7)
  }));

  const favoritesData = Array.from({ length: 6 }, (_, i) => ({
    name: `M${i + 1}`,
    favorites: Math.floor((realUserStats.totalFavorites / 6) * (i + 1))
  }));

  const totalAdsData = Array.from({ length: 6 }, (_, i) => ({
    name: `M${i + 1}`,
    ads: Math.floor((realUserStats.totalAds / 6) * (i + 1))
  }));

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size should be less than 5MB');
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image');
        return;
      }

      setIsUploadingImage(true);
      uploadImageMutation.mutate(file);
    }
  };

  const sidebarItems = [
    { id: 'plans', label: 'پلن‌های من', icon: DocumentIcon },
    { id: 'profile', label: 'پروفایل', icon: UserIcon },
    { id: 'listings', label: 'آگهی‌های من', icon: ListBulletIcon, badge: (userAds?.data?.length && userAds.data.length > 0) ? userAds.data.length : undefined },
    { id: 'analytics', label: 'آمار و تحلیل', icon: ChartBarIcon },
    { id: 'favorites', label: 'علاقه‌مندی‌ها', icon: HeartIcon, badge: (favoriteAds?.data?.totalCount !== undefined && favoriteAds.data.totalCount !== null && typeof favoriteAds.data.totalCount === 'number' && favoriteAds.data.totalCount > 0) ? favoriteAds.data.totalCount : undefined },
    { id: 'payments', label: 'جزئیات پرداخت', icon: CreditCardIcon },
    { id: 'settings', label: 'تنظیمات حساب', icon: CogIcon },
    { id: 'help', label: 'راهنما', icon: QuestionMarkCircleIcon },
  ];

  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/');
      toast.success('با موفقیت خارج شدید', { style: { direction: 'rtl', textAlign: 'right' } });
    } catch (error) {
      toast.error('خروج ناموفق بود', { style: { direction: 'rtl', textAlign: 'right' } });
    }
  };

  const handleBumpUpAd = async (adId: string) => {
    setBoostingAds(prev => new Set(prev).add(adId));
    try {
      const response = await adsApi.bumpUpAd(adId);
      toast.success(response.data.message);
      // Refresh the ads list to show updated position
      queryClient.invalidateQueries({ queryKey: ['user-ads', id || currentUser?.id] });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error bumping up ad');
    } finally {
      setBoostingAds(prev => {
        const newSet = new Set(prev);
        newSet.delete(adId);
        return newSet;
      });
    }
  };

  const handleMarkAdAsSold = async (adId: string) => {
    setMarkingSoldAds(prev => new Set(prev).add(adId));
    try {
      const response = await adsApi.markAsSold(adId);
      toast.success(response.data.message);
      // Refresh the ads list to show updated status
      queryClient.invalidateQueries({ queryKey: ['user-ads', id || currentUser?.id] });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error marking ad as sold');
    } finally {
      setMarkingSoldAds(prev => {
        const newSet = new Set(prev);
        newSet.delete(adId);
        return newSet;
      });
    }
  };

  const handleApplyTopAd = async (adId: string) => {
    const promotionKey = `${adId}-top`;
    setApplyingPromotions(prev => new Set(prev).add(promotionKey));
    try {
      const response = await adsApi.applyTopAd(adId);
      toast.success(response.data.message);
      // Refresh user plans and ads to show updated counts
      queryClient.invalidateQueries({ queryKey: ['user-plans'] });
      queryClient.invalidateQueries({ queryKey: ['user-ads', id || currentUser?.id] });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'خطا در اعمال آگهی برتر', { style: { direction: 'rtl', textAlign: 'right' } });
    } finally {
      setApplyingPromotions(prev => {
        const newSet = new Set(prev);
        newSet.delete(promotionKey);
        return newSet;
      });
    }
  };

  const handleApplyHighlight = async (adId: string) => {
    const promotionKey = `${adId}-highlight`;
    setApplyingPromotions(prev => new Set(prev).add(promotionKey));
    try {
      const response = await adsApi.applyHighlight(adId);
      toast.success(response.data.message);
      // Refresh user plans and ads to show updated counts
      queryClient.invalidateQueries({ queryKey: ['user-plans'] });
      queryClient.invalidateQueries({ queryKey: ['user-ads', id || currentUser?.id] });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'خطا در اعمال برجسته', { style: { direction: 'rtl', textAlign: 'right' } });
    } finally {
      setApplyingPromotions(prev => {
        const newSet = new Set(prev);
        newSet.delete(promotionKey);
        return newSet;
      });
    }
  };

  const handleApplyUrgentTag = async (adId: string) => {
    const promotionKey = `${adId}-urgent`;
    setApplyingPromotions(prev => new Set(prev).add(promotionKey));
    try {
      const response = await adsApi.applyUrgentTag(adId);
      toast.success(response.data.message);
      // Refresh user plans and ads to show updated counts
      queryClient.invalidateQueries({ queryKey: ['user-plans'] });
      queryClient.invalidateQueries({ queryKey: ['user-ads', id || currentUser?.id] });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'خطا در اعمال برچسب فوری', { style: { direction: 'rtl', textAlign: 'right' } });
    } finally {
      setApplyingPromotions(prev => {
        const newSet = new Set(prev);
        newSet.delete(promotionKey);
        return newSet;
      });
    }
  };

  // Business profile functions
  const handleBusinessFormChange = (field: keyof UpdateBusinessProfileRequest, value: any) => {
    setBusinessFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };



  const handleGalleryUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setIsUploadingGallery(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const response = await businessApi.uploadGalleryImage(file);
        return response.data.imageUrl;
      });

      const uploadedImageUrls = await Promise.all(uploadPromises);
      
      toast.success(`${files.length} images uploaded successfully and are pending admin approval`);
      
      // Refresh current user data to get updated gallery images from database
      await queryClient.invalidateQueries({ queryKey: ['current-user'] });
      
    } catch (error: any) {
      console.error('Error uploading gallery images:', error);
      toast.error(error.response?.data?.message || 'Error uploading images');
    } finally {
      setIsUploadingGallery(false);
      if (galleryInputRef.current) {
        galleryInputRef.current.value = '';
      }
    }
  };

  const handleDeleteGalleryImage = async (imageUrl: string) => {
    try {
      await businessApi.deleteGalleryImage(imageUrl);
      toast.success('Image deleted');
      
      // Refresh current user data to get updated gallery images from database
      await queryClient.invalidateQueries({ queryKey: ['current-user'] });
      
    } catch (error) {
      console.error('Error deleting gallery image:', error);
      toast.error('خطا در حذف تصویر', {
        style: { direction: 'rtl', textAlign: 'right' }
      });
    }
  };

  const handleBusinessHoursChange = (day: keyof BusinessHours, field: 'open' | 'close' | 'closed', value: string | boolean) => {
    setBusinessFormData(prev => ({
      ...prev,
      businessHours: {
        ...prev.businessHours!,
        [day]: {
          ...prev.businessHours![day],
          [field]: value
        }
      }
    }));
  };

  const handleSaveBusinessProfile = async () => {
    setIsSavingBusiness(true);
    try {
      const response = await userApi.updateBusinessProfile({
        businessName: businessFormData.businessName,
        businessDescription: businessFormData.businessDescription,
        businessWebsite: businessFormData.businessWebsite,
        businessAddress: businessFormData.businessAddress,
        businessHours: businessFormData.businessHours
      });
      
      // Show success message for approval workflow
      toast.success(response.data.message, {
        duration: 6000, // Show longer since it's important information
        style: {
          background: '#10B981',
          color: '#ffffff',
          fontFamily: 'Arial, sans-serif',
          textAlign: 'left',
          direction: 'ltr',
          borderRadius: '12px',
          fontSize: '14px',
          fontWeight: '500',
          padding: '16px',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
        }
      });
      
      // Update the user data in React Query cache
      queryClient.setQueryData(['current-user'], (oldData: any) => ({
        ...oldData,
        data: {
          ...response.data.user,
          businessGallery: businessGallery // Include the current gallery state
        }
      }));
      
      // Refresh user data
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
    } catch (error: any) {
      console.error('Error saving business profile:', error);
      toast.error(error.response?.data?.message || 'خطا در ذخیره اطلاعات کسب‌وکار', { style: { direction: 'rtl', textAlign: 'right' } });
    } finally {
      setIsSavingBusiness(false);
    }
  };

  const handleSaveGeneralProfile = async () => {
    setIsSavingGeneral(true);
    try {
      const response = await userApi.updateProfile({
        firstName: generalFormData.firstName,
        lastName: generalFormData.lastName,
        phoneNumber: generalFormData.phoneNumber
      });
      
      toast.success(response.data.message);
      
      // Update the user data in React Query cache
      queryClient.setQueryData(['current-user'], (oldData: any) => ({
        ...oldData,
        data: response.data.user
      }));
      
      // Refresh user data
      queryClient.invalidateQueries({ queryKey: ['current-user'] });
    } catch (error: any) {
      console.error('Error saving general profile:', error);
      toast.error(error.response?.data?.message || 'خطا در ذخیره اطلاعات عمومی', { style: { direction: 'rtl', textAlign: 'right' } });
    } finally {
      setIsSavingGeneral(false);
    }
  };

  // Advanced filters handlers
  const handleAdvancedFilterChange = (field: string, value: any) => {
    setAdvancedFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAdvancedFilterRangeChange = (rangeType: string, field: string, value: string) => {
    setAdvancedFilters(prev => {
      const currentRange = prev[rangeType as keyof typeof prev] as any;
      return {
        ...prev,
        [rangeType]: {
          ...currentRange,
          [field]: value
        }
      };
    });
  };

  const handlePromotionalFeatureChange = (feature: string, value: boolean) => {
    setAdvancedFilters(prev => ({
      ...prev,
      promotionalFeatures: {
        ...prev.promotionalFeatures,
        [feature]: value
      }
    }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      dateRange: { start: '', end: '' },
      priceRange: { min: '', max: '' },
      viewsRange: { min: '', max: '' },
      favoritesRange: { min: '', max: '' },
      sortBy: 'datePosted',
      sortOrder: 'desc',
      promotionalFeatures: {
        isTopAd: false,
        isHighlighted: false,
        isUrgent: false,
        isFeatured: false
      }
    });
  };

  const applyAdvancedFilters = () => {
    setShowAdvancedFilters(false);
    setCurrentPage(1); // Reset to first page when applying filters
    toast.success('فیلترهای پیشرفته اعمال شد');
  };

  const hasActiveAdvancedFilters = () => {
    return advancedFilters.dateRange.start || advancedFilters.dateRange.end ||
           advancedFilters.priceRange.min || advancedFilters.priceRange.max ||
           advancedFilters.viewsRange.min || advancedFilters.viewsRange.max ||
           advancedFilters.favoritesRange.min || advancedFilters.favoritesRange.max ||
           Object.values(advancedFilters.promotionalFeatures).some(Boolean) ||
           advancedFilters.sortBy !== 'datePosted' || advancedFilters.sortOrder !== 'desc';
  };

  const user = currentUserData?.data || currentUser;

  const translatePlanName = (name: string) => {
    const translations: { [key: string]: string } = {
      'Free Plan ': 'پلن رایگان',
      'Featured Plan ': 'پلن ویژه', 
      'Gold Plan ': 'پلن طلایی',
      'Free Package': 'پلن رایگان',
      'Premium Package': 'پلن ویژه',
      'Gold Package': 'پلن طلایی',
      'Free Plan': 'پلن رایگان',
      'Premium Plan': 'پلن ویژه',
      'Gold Plan': 'پلن طلایی'
    };
    return translations[name.trim()] || name;
  };

  const translatePlanDescription = (description: string) => {
    const translations: { [key: string]: string } = {
      ' Standard ad with basic features   ': 'آگهی استاندارد با ویژگی‌های پایه',
      'Standard ad with basic features': 'آگهی استاندارد با ویژگی‌های پایه',
      'Featured ad with advanced features': 'آگهی ویژه با ویژگی‌های پیشرفته',
      'Maximum visibility with all features': 'حداکثر نمایش با تمام ویژگی‌ها'
    };
    return translations[description.trim()] || description;
  };

  if (isLoadingAds) {
    return (
      <div className="min-h-screen bg-white flex">
        <div className="w-80 bg-white shadow-sm">
          <div className="p-6 border-b">
            <div className="animate-pulse space-y-4">
              <div className="bg-gray-200 rounded-full h-20 w-20 mx-auto"></div>
              <div className="bg-gray-200 rounded h-4 w-3/4 mx-auto"></div>
              <div className="bg-gray-200 rounded h-3 w-1/2 mx-auto"></div>
            </div>
          </div>
          <div className="p-6 space-y-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-gray-200 rounded h-10"></div>
            ))}
          </div>
        </div>
        <div className="flex-1 p-8">
          <div className="animate-pulse space-y-6">
            <div className="bg-gray-200 rounded h-8 w-1/4"></div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="bg-gray-200 rounded-lg h-32"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      <div className="flex flex-col lg:flex-row">
        {/* Enhanced Sidebar - Responsive */}
        <div className="w-full lg:w-80 bg-white shadow-lg border-b lg:border-l lg:border-b-0 border-gray-200 lg:min-h-screen">
          {/* Profile Header - Responsive */}
          <div className="p-4 lg:p-6 border-b border-gray-200 bg-gradient-to-br from-blue-50 to-indigo-100">
            <div className="text-center">
              <div className="relative inline-block">
                <div className="w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white text-lg lg:text-2xl font-semibold shadow-lg">
                  {isUploadingImage ? (
                    <div className="animate-spin rounded-full h-6 w-6 lg:h-8 lg:w-8 border-b-2 border-white"></div>
                  ) : user?.profileImageUrl ? (
                    <img 
                      src={user.profileImageUrl} 
                      alt="Profile" 
                      className="w-full h-full rounded-full object-cover"
                    />
                  ) : (
                    `${user?.firstName?.[0] || 'U'}${user?.lastName?.[0] || ''}`
                  )}
                </div>
                {/* Profile image upload removed per request - users cannot upload profile images here */}
              </div>
              <div className="mt-3 lg:mt-4">
                <h2 className="text-lg lg:text-xl font-semibold text-gray-900">
                  {user?.firstName} {user?.lastName}
                </h2>
                <p className="text-xs lg:text-sm text-gray-600 break-all">{user?.email}</p>
                <div className="flex items-center justify-center mt-1 lg:mt-2 text-xs text-gray-500">
                  <ClockIcon className="h-3 w-3 lg:h-4 lg:w-4 ml-1" />
                  عضویت در {new Date(user?.dateJoined || '').toLocaleDateString('fa-IR')}
                </div>
              </div>
            </div>
          </div>

          {/* Quick Stats - Responsive */}
          <div className="p-3 lg:p-4 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-2 gap-2 lg:gap-4">
              <div className="text-center">
                <div className="text-lg lg:text-xl font-semibold text-primary-600">{realUserStats.totalAds}</div>
                <div className="text-xs text-gray-600">کل آگهی‌ها</div>
              </div>
              <div className="text-center">
                <div className="text-lg lg:text-xl font-semibold text-green-600">{realUserStats.activeAds}</div>
                <div className="text-xs text-gray-600">فعال</div>
              </div>
            </div>
          </div>

          {/* Navigation - Responsive */}
          <nav className="p-3 lg:p-4 space-y-1">
            {sidebarItems.map((item) => {
              const Icon = item.icon;
              return (
                                <button
                  key={item.id}
                  onClick={() => handleTabSwitch(item.id)}
                  disabled={isSwitchingTab}
                  className={`w-full flex items-center justify-between px-3 lg:px-4 py-2 lg:py-3 text-right rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                    activeTab === item.id
                      ? 'bg-primary-50 text-primary-700 border-r-4 border-primary-700 shadow-sm'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <div className="flex items-center">
                    <Icon className={`h-4 w-4 lg:h-5 lg:w-5 ml-2 lg:ml-3 ${
                      activeTab === item.id ? 'text-primary-600' : 'text-gray-500'
                    }`} />
                    <span className="font-medium text-sm lg:text-base">{item.label}</span>
                  </div>
                  {item.badge !== undefined && item.badge !== null && item.badge > 0 && (
                    <span className="bg-primary-100 text-primary-800 text-xs font-medium px-2 lg:px-2.5 py-0.5 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Sign Out */}
          <div className="p-4 border-t border-gray-200 mt-auto">
            <button
              onClick={() => {
                showModal({
                  title: 'خروج',
                  message: 'آیا مطمئن هستید که می‌خواهید از حساب خود خارج شوید؟',
                  confirmText: 'خروج',
                  cancelText: 'لغو',
                  type: 'warning',
                  isRtl: false,
                  onConfirm: handleSignOut
                });
              }}
              className="w-full flex items-center px-4 py-3 text-right text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <ArrowRightOnRectangleIcon className="h-5 w-5 ml-3" />
              <span className="font-medium">خروج</span>
            </button>
          </div>
        </div>

        {/* Main Content - Responsive */}
        <div className="flex-1 overflow-auto relative">
          {isSwitchingTab && (
            <TimchehLoader 
              fullScreen={false}
              size="large" 
              message={getTabLoadingMessage(activeTab)} 
              className="absolute inset-0 bg-white/95 backdrop-blur-sm z-50"
            />
          )}
          <div className="p-4 sm:p-6 lg:p-8">
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className="space-y-6 lg:space-y-8 animate-fade-in">
                {/* Floating background elements - Hidden on mobile */}
                <div className="hidden lg:block fixed inset-0 pointer-events-none overflow-hidden">
                  <div className="absolute top-20 right-20 w-72 h-72 bg-gradient-to-r from-blue-400/20 to-purple-400/20 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
                  <div className="absolute top-40 left-20 w-64 h-64 bg-gradient-to-r from-yellow-400/20 to-pink-400/20 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
                  <div className="absolute bottom-20 right-40 w-80 h-80 bg-gradient-to-r from-green-400/20 to-blue-400/20 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
                </div>

                <div className="relative backdrop-blur-sm bg-white/40 border border-white/50 rounded-2xl lg:rounded-3xl p-4 sm:p-6 lg:p-8 shadow-2xl">
                  {/* Advanced Header with Persian patterns - Responsive */}
                  <div className="relative overflow-hidden rounded-xl lg:rounded-2xl p-4 sm:p-6 lg:p-8 mb-6 lg:mb-10 bg-gradient-to-r from-indigo-600 via-purple-600 via-pink-600 to-red-600 shadow-2xl">
                    {/* Animated background pattern */}
                    <div className="absolute inset-0 bg-black/20"></div>
                    <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 translate-x-full animate-shimmer"></div>
                    
                    {/* Persian geometric patterns - Hidden on mobile */}
                    <div className="hidden lg:block absolute inset-0 opacity-10">
                      <div className="absolute top-4 right-4 w-16 h-16 border-2 border-white transform rotate-45 animate-spin-slow"></div>
                      <div className="absolute bottom-4 left-4 w-12 h-12 border-2 border-white/60 transform rotate-12 animate-pulse"></div>
                      <div className="absolute top-1/2 left-1/2 w-20 h-20 border border-white/40 rounded-full transform -translate-x-1/2 -translate-y-1/2 animate-ping"></div>
                    </div>
                    
                    <div className="relative z-10">
                      <div className="flex items-center space-x-2 lg:space-x-4 mb-3 lg:mb-4">
                        <div className="w-2 h-2 lg:w-3 lg:h-3 bg-white rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 lg:w-3 lg:h-3 bg-white/80 rounded-full animate-bounce animation-delay-200"></div>
                        <div className="w-2 h-2 lg:w-3 lg:h-3 bg-white/60 rounded-full animate-bounce animation-delay-400"></div>
                      </div>
                      <h1 className="text-2xl sm:text-3xl lg:text-5xl font-extrabold text-white mb-2 lg:mb-3 drop-shadow-2xl bg-gradient-to-r from-white to-blue-100 bg-clip-text">پروفایل</h1>
                      <p className="text-blue-100 text-sm sm:text-base lg:text-xl font-medium">✨ مدیریت اطلاعات شخصی و آمار حساب کاربری ✨</p>
                    </div>
                    
                    {/* Floating decorative elements - Hidden on mobile */}
                    <div className="hidden lg:block absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-white/20 to-transparent rounded-full transform translate-x-20 -translate-y-20 animate-float"></div>
                    <div className="hidden lg:block absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-white/15 to-transparent rounded-full transform -translate-x-16 translate-y-16 animate-float-delay"></div>
                    <div className="hidden lg:block absolute top-1/2 right-10 w-6 h-6 bg-white/40 rounded-full animate-pulse"></div>
                    <div className="hidden lg:block absolute top-1/4 left-20 w-4 h-4 bg-yellow-300/60 rounded-full animate-ping"></div>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12">
                    {/* Personal Information - Responsive */}
                    <div className="space-y-4 lg:space-y-8">
                      <div className="flex items-center space-x-2 lg:space-x-3">
                        <div className="p-1.5 lg:p-2 bg-gradient-to-r mr-4 from-blue-500 to-purple-600 rounded-lg">
                          <UserIcon className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
                        </div>
                        <h2 className="text-xl lg:text-2xl font-semibold text-gray-900">اطلاعات شخصی</h2>
                      </div>
                      
                      <div className="space-y-4 lg:space-y-6">
                        <div className="group hover:bg-blue-50/50 p-3 lg:p-4 rounded-xl transition-all duration-300 border border-transparent hover:border-blue-200">
                          <label className="text-xs lg:text-sm font-semibold text-gray-600 uppercase tracking-wider">نام کامل</label>
                          <div className="mt-2 flex items-center text-gray-900">
                            <div className="p-1.5 lg:p-2 bg-blue-100 rounded-lg mr-2 lg:mr-3 group-hover:bg-blue-200 transition-colors">
                              <UserIcon className="h-4 w-4 lg:h-5 lg:w-5 text-blue-600" />
                            </div>
                            <span className="text-base lg:text-lg font-medium">{user?.firstName} {user?.lastName}</span>
                          </div>
                        </div>
                        
                        <div className="group hover:bg-green-50/50 p-3 lg:p-4 rounded-xl transition-all duration-300 border border-transparent hover:border-green-200">
                          <label className="text-xs lg:text-sm font-semibold text-gray-600 uppercase tracking-wider">ایمیل</label>
                          <div className="mt-2 flex items-center text-gray-900">
                            <div className="p-1.5 lg:p-2 bg-green-100 rounded-lg mr-2 lg:mr-3 group-hover:bg-green-200 transition-colors">
                              <EnvelopeIcon className="h-4 w-4 lg:h-5 lg:w-5 text-green-600" />
                            </div>
                            <span className="text-base lg:text-lg font-medium break-all">{user?.email}</span>
                          </div>
                        </div>
                        
                        <div className="group hover:bg-purple-50/50 p-3 lg:p-4 rounded-xl transition-all duration-300 border border-transparent hover:border-purple-200">
                          <label className="text-xs lg:text-sm font-semibold text-gray-600 uppercase tracking-wider">شماره تلفن</label>
                          <div className="mt-2 flex items-center text-gray-900">
                            <div className="p-1.5 lg:p-2 bg-purple-100 rounded-lg mr-2 lg:mr-3 group-hover:bg-purple-200 transition-colors">
                              <PhoneIcon className="h-4 w-4 lg:h-5 lg:w-5 text-purple-600" />
                            </div>
                            <span className="text-base lg:text-lg font-medium">{user?.phoneNumber || 'Not provided'}</span>
                          </div>
                        </div>
                        
                        <div className="group hover:bg-orange-50/50 p-3 lg:p-4 rounded-xl transition-all duration-300 border border-transparent hover:border-orange-200">
                          <label className="text-xs lg:text-sm font-semibold text-gray-600 uppercase tracking-wider">نوع کاربر</label>
                          <div className="mt-2 flex items-center justify-between text-gray-900">
                            <div className="flex items-center">
                                                          <div className="p-1.5 lg:p-2 bg-orange-100 rounded-lg mr-2 lg:mr-3 group-hover:bg-orange-200 transition-colors">
                              <BuildingOfficeIcon className="h-4 w-4 lg:h-5 lg:w-5 text-orange-600" />
                            </div>
                              <span className={`px-3 lg:px-4 py-1.5 lg:py-2 rounded-full text-xs lg:text-sm font-semibold shadow-sm ${
                                user?.userType === 'Business' 
                                  ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white' 
                                  : 'bg-gradient-to-r from-green-500 to-green-600 text-white'
                              }`}>
                                {user?.userType === 'Business' ? 'شرکتی' : 'شخصی'}
                              </span>
                            </div>
                            {user?.userType === 'Business' && user?.businessApprovalStatus === 1 && (
                              <Link
                                to={`/business/${user.businessSlug || 'business'}`}
                                className="flex items-center px-3 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-xs lg:text-sm font-medium rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-md hover:shadow-lg"
                              >
                                <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                                مشاهده شرکت
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Account Statistics - Responsive */}
                    <div className="space-y-4 lg:space-y-8">
                      <div className="flex items-center space-x-2 lg:space-x-3">
                        <div className="p-1.5 lg:p-2 bg-gradient-to-r mr-4 from-green-500 to-blue-600 rounded-lg">
                          <ChartBarIcon className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
                        </div>
                        <h2 className="text-xl lg:text-2xl  font-semibold text-gray-900">آمار حساب کاربری</h2>
                      </div>
                      
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-8">
                         {/* Total Ads Card with Chart */}
                         <div className="group hover:scale-105 lg:hover:scale-110 hover:-rotate-1 transition-all duration-500 cursor-pointer">
                           <div className="relative backdrop-blur-md bg-gradient-to-br from-red-500/90 via-pink-500/90 to-red-600/90 rounded-lg lg:rounded-xl p-4 lg:p-8 text-white shadow-2xl hover:shadow-red-500/25 transition-all duration-500 border border-white/20 overflow-hidden">
                             {/* Glassmorphism overlay */}
                             <div className="absolute inset-0 bg-white/10 rounded-lg lg:rounded-xl backdrop-blur-sm"></div>
                             {/* Animated background particles */}
                             <div className="absolute top-2 right-2 w-2 h-2 bg-white/60 rounded-full animate-ping"></div>
                             <div className="absolute bottom-4 left-4 w-1 h-1 bg-red-200 rounded-full animate-bounce animation-delay-1000"></div>
                             
                             <div className="relative z-10">
                               <div className="flex items-center justify-between mb-3 lg:mb-4">
                                 <div className="p-2 lg:p-3 bg-white/20 rounded-xl lg:rounded-2xl backdrop-blur-sm group-hover:bg-white/30 transition-colors duration-300">
                                   <ListBulletIcon className="h-5 w-5 lg:h-7 lg:w-7 text-white drop-shadow-lg group-hover:scale-110 transition-transform duration-300" />
                                 </div>
                                                                 <div className="text-left">
                                  <div className="text-2xl lg:text-4xl font-semibold bg-gradient-to-r from-white to-red-100 bg-clip-text text-transparent drop-shadow-lg">
                                    {realUserStats.totalAds}
                                  </div>
                                  <div className="text-red-100 text-xs lg:text-sm font-semibold tracking-wider uppercase">کل آگهی‌ها</div>
                                </div>
                               </div>
                               {/* Beautiful Area Chart */}
                               <div className="h-16 lg:h-20 mt-4">
                                 <ResponsiveContainer width="100%" height="100%">
                                   <AreaChart data={totalAdsData}>
                                     <Area 
                                       type="monotone" 
                                       dataKey="ads" 
                                       stroke="rgba(255,255,255,0.8)" 
                                       strokeWidth={2}
                                       fill="rgba(255,255,255,0.3)"
                                       fillOpacity={0.6}
                                     />
                                   </AreaChart>
                                 </ResponsiveContainer>
                               </div>
                             </div>
                           </div>
                         </div>

                         {/* Active Ads Card with Pie Chart */}
                         <div className="group hover:scale-105 lg:hover:scale-110 hover:rotate-1 transition-all duration-500 cursor-pointer">
                           <div className="relative backdrop-blur-md bg-gradient-to-br from-emerald-500/90 via-green-500/90 to-teal-600/90 rounded-lg lg:rounded-xl p-4 lg:p-8 text-white shadow-2xl hover:shadow-green-500/25 transition-all duration-500 border border-white/20 overflow-hidden">
                             <div className="absolute inset-0 bg-white/10 rounded-lg lg:rounded-xl backdrop-blur-sm"></div>
                             <div className="absolute top-4 left-2 w-2 h-2 bg-green-200/80 rounded-full animate-ping animation-delay-500"></div>
                             <div className="absolute bottom-2 right-6 w-1 h-1 bg-white/70 rounded-full animate-bounce animation-delay-700"></div>
                             
                             <div className="relative z-10">
                               <div className="flex items-center justify-between mb-3 lg:mb-4">
                                 <div className="p-2 lg:p-3 bg-white/20 rounded-xl lg:rounded-2xl backdrop-blur-sm group-hover:bg-white/30 transition-colors duration-300">
                                   <StarIcon className="h-5 w-5 lg:h-7 lg:w-7 text-white drop-shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-300" />
                                 </div>
                                                                 <div className="text-left">
                                  <div className="text-2xl lg:text-4xl font-semibold bg-gradient-to-r from-white to-green-100 bg-clip-text text-transparent drop-shadow-lg">
                                    {realUserStats.activeAds}
                                  </div>
                                  <div className="text-green-100 text-xs lg:text-sm font-semibold tracking-wider uppercase">آگهی‌های فعال</div>
                                </div>
                               </div>
                               {/* Beautiful Pie Chart */}
                               <div className="h-16 lg:h-20 mt-4 flex justify-center">
                                 <ResponsiveContainer width="100%" height="100%">
                                   <PieChart>
                                     <Pie
                                       data={adsChartData}
                                       cx="50%"
                                       cy="50%"
                                       innerRadius={25}
                                       outerRadius={35}
                                       dataKey="value"
                                       startAngle={90}
                                       endAngle={450}
                                     >
                                       <Cell fill="rgba(255,255,255,0.8)" />
                                       <Cell fill="rgba(255,255,255,0.2)" />
                                     </Pie>
                                   </PieChart>
                                 </ResponsiveContainer>
                               </div>
                             </div>
                           </div>
                         </div>

                         {/* Favorites Card with Line Chart */}
                         <div className="group hover:scale-110 hover:-rotate-1 transition-all duration-500 cursor-pointer">
                           <div className="relative backdrop-blur-md bg-gradient-to-br from-orange-500/90 via-amber-500/90 to-yellow-600/90 rounded-lg lg:rounded-xl p-4 lg:p-8 text-white shadow-2xl hover:shadow-orange-500/25 transition-all duration-500 border border-white/20 overflow-hidden">
                             <div className="absolute inset-0 bg-white/10 rounded-lg lg:rounded-xl backdrop-blur-sm"></div>
                             <div className="absolute top-6 right-6 w-1 h-1 bg-yellow-200 rounded-full animate-ping animation-delay-1500"></div>
                             <div className="absolute bottom-6 left-2 w-2 h-2 bg-orange-200/60 rounded-full animate-bounce animation-delay-2000"></div>
                             
                             <div className="relative z-10">
                               <div className="flex items-center justify-between mb-3 lg:mb-4">
                                 <div className="p-2 lg:p-3 bg-white/20 rounded-xl lg:rounded-2xl backdrop-blur-sm group-hover:bg-white/30 transition-colors duration-300">
                                   <HeartIcon className="h-5 w-5 lg:h-7 lg:w-7 text-white drop-shadow-lg group-hover:scale-110 group-hover:animate-pulse transition-all duration-300" />
                                 </div>
                                                                 <div className="text-left">
                                  <div className="text-2xl lg:text-4xl font-semibold bg-gradient-to-r from-white to-orange-100 bg-clip-text text-transparent drop-shadow-lg">
                                    {realUserStats.totalFavorites}
                                  </div>
                                  <div className="text-orange-100 text-xs lg:text-sm font-semibold tracking-wider uppercase">علاقه‌مندی‌ها</div>
                                </div>
                               </div>
                               {/* Beautiful Line Chart */}
                               <div className="h-16 lg:h-20 mt-4">
                                 <ResponsiveContainer width="100%" height="100%">
                                   <LineChart data={favoritesData}>
                                     <Line 
                                       type="monotone" 
                                       dataKey="favorites" 
                                       stroke="rgba(255,255,255,0.9)" 
                                       strokeWidth={2}
                                       dot={{ fill: 'rgba(255,255,255,0.8)', strokeWidth: 0, r: 3 }}
                                       activeDot={{ r: 4, fill: 'white' }}
                                     />
                                   </LineChart>
                                 </ResponsiveContainer>
                               </div>
                             </div>
                           </div>
                         </div>

                         {/* Total Views Card with Bar Chart */}
                         <div className="group hover:scale-110 hover:rotate-1 transition-all duration-500 cursor-pointer">
                           <div className="relative backdrop-blur-md bg-gradient-to-br from-purple-500/90 via-violet-500/90 to-indigo-600/90 rounded-lg lg:rounded-xl p-4 lg:p-8 text-white shadow-2xl hover:shadow-purple-500/25 transition-all duration-500 border border-white/20 overflow-hidden">
                             <div className="absolute inset-0 bg-white/10 rounded-lg lg:rounded-xl backdrop-blur-sm"></div>
                             <div className="absolute top-3 left-3 w-2 h-2 bg-purple-200/70 rounded-full animate-ping animation-delay-800"></div>
                             <div className="absolute bottom-3 right-3 w-1 h-1 bg-indigo-200 rounded-full animate-bounce animation-delay-1200"></div>
                             
                             <div className="relative z-10">
                               <div className="flex items-center justify-between mb-3 lg:mb-4">
                                 <div className="p-2 lg:p-3 bg-white/20 rounded-xl lg:rounded-2xl backdrop-blur-sm group-hover:bg-white/30 transition-colors duration-300">
                                   <EyeIcon className="h-5 w-5 lg:h-7 lg:w-7 text-white drop-shadow-lg group-hover:scale-110 transition-transform duration-300" />
                                 </div>
                                                                 <div className="text-left">
                                  <div className="text-2xl lg:text-4xl font-semibold bg-gradient-to-r from-white to-purple-100 bg-clip-text text-transparent drop-shadow-lg">
                                    {realUserStats.totalViews.toLocaleString('fa-IR')}
                                  </div>
                                  <div className="text-purple-100 text-xs lg:text-sm font-semibold tracking-wider uppercase">کل بازدیدها</div>
                                </div>
                               </div>
                               {/* Beautiful Bar Chart */}
                               <div className="h-16 lg:h-20 mt-4">
                                 <ResponsiveContainer width="100%" height="100%">
                                   <BarChart data={viewsChartData}>
                                     <Bar 
                                       dataKey="views" 
                                       fill="rgba(255,255,255,0.7)"
                                       radius={[2, 2, 0, 0]}
                                     />
                                   </BarChart>
                                 </ResponsiveContainer>
                               </div>
                             </div>
                           </div>
                         </div>
                       </div>
                    </div>
                  </div>

                  <div className="mt-16 pt-10 border-t border-white/30 relative">
                    {/* Floating action area */}
                    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent"></div>
                    
                    <div className="flex justify-center items-center space-x-6">
                      {/* Simple edit button */}
                      <Link to="?tab=settings" className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 flex items-center space-x-2 shadow-md">
                        <PencilIcon className="h-5 w-5" />
                        <span>ویرایش پروفایل</span>
                      </Link>
                      
                      {/* Floating decorative elements around button */}
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <div className="flex space-x-2">
                          <div className="w-2 h-2 bg-purple-400/60 rounded-full animate-bounce animation-delay-0"></div>
                          <div className="w-1.5 h-1.5 bg-pink-400/60 rounded-full animate-bounce animation-delay-200"></div>
                          <div className="w-2 h-2 bg-blue-400/60 rounded-full animate-bounce animation-delay-400"></div>
                        </div>
                      </div>
                    </div>

                  </div>
                </div>
              </div>
            )}

            {/* Analytics Tab */}
            {activeTab === 'analytics' && (
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <h1 className="text-3xl font-semibold text-gray-900">داشبورد آمار و تحلیل</h1>
                                      <select
                      value={viewPeriod}
                      onChange={(e) => setViewPeriod(Number(e.target.value))}
                                                  className="border border-gray-300 rounded-lg px-4 py-2 bg-white text-right appearance-none pl-10 pr-4"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'left 0.75rem center',
                        backgroundRepeat: 'no-repeat',
                        backgroundSize: '1.25rem 1.25rem'
                      }}
                    >
                    <option value={7}>۷ روز گذشته</option>
                    <option value={30}>۳۰ روز گذشته</option>
                    <option value={90}>۹۰ روز گذشته</option>
                  </select>
                </div>

                {/* Enhanced Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-primary-100 text-sm">کل بازدیدها</p>
                        <p className="text-2xl font-semibold">{realUserStats.totalViews.toLocaleString('en-US')}</p>
                      </div>
                      <EyeIcon className="h-10 w-10 text-primary-200" />
                    </div>
                    <div className="flex items-center mt-4">
                      {calculatePercentageChange('views').isPositive ? (
                        <ArrowTrendingUpIcon className="h-4 w-4 ml-1" />
                      ) : (
                        <ArrowTrendingDownIcon className="h-4 w-4 ml-1" />
                      )}
                      <span className="text-sm">
                        {calculatePercentageChange('views').isPositive ? '+' : '-'}{calculatePercentageChange('views').percentage}% نسبت به ماه گذشته
                      </span>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">آگهی‌های فعال</p>
                        <p className="text-2xl font-semibold">{realUserStats.activeAds}</p>
                      </div>
                      <ListBulletIcon className="h-10 w-10 text-green-200" />
                    </div>
                    <div className="flex items-center mt-4">
                      {calculatePercentageChange('ads').isPositive ? (
                        <ArrowTrendingUpIcon className="h-4 w-4 ml-1" />
                      ) : (
                        <ArrowTrendingDownIcon className="h-4 w-4 ml-1" />
                      )}
                      <span className="text-sm">
                        {calculatePercentageChange('ads').isPositive ? '+' : '-'}{calculatePercentageChange('ads').percentage}% نسبت به ماه گذشته
                      </span>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm">کل علاقه‌مندی‌ها</p>
                        <p className="text-2xl font-semibold">{realUserStats.totalFavorites}</p>
                      </div>
                      <HeartIcon className="h-10 w-10 text-purple-200" />
                    </div>
                    <div className="flex items-center mt-4">
                      {calculatePercentageChange('favorites').isPositive ? (
                        <ArrowTrendingUpIcon className="h-4 w-4 ml-1" />
                      ) : (
                        <ArrowTrendingDownIcon className="h-4 w-4 ml-1" />
                      )}
                      <span className="text-sm">
                        {calculatePercentageChange('favorites').isPositive ? '+' : '-'}{calculatePercentageChange('favorites').percentage}% نسبت به ماه گذشته
                      </span>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-6 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-100 text-sm">آگهی‌های فروخته شده</p>
                        <p className="text-2xl font-semibold">{realUserStats.soldAds}</p>
                      </div>
                      <ChartBarIcon className="h-10 w-10 text-orange-200" />
                    </div>
                    <div className="flex items-center mt-4">
                      {calculatePercentageChange('soldAds').isPositive ? (
                        <ArrowTrendingUpIcon className="h-4 w-4 ml-1" />
                      ) : (
                        <ArrowTrendingDownIcon className="h-4 w-4 ml-1" />
                      )}
                      <span className="text-sm">
                        {calculatePercentageChange('soldAds').isPositive ? '+' : '-'}{calculatePercentageChange('soldAds').percentage}% نسبت به ماه گذشته
                      </span>
                    </div>
                  </div>
                </div>

                {/* Enhanced Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Views Over Time Chart */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">روند بازدیدها و علاقه‌مندی‌ها</h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={chartData.adViews}>
                        <defs>
                          <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                          </linearGradient>
                          <linearGradient id="favoritesGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="date" stroke="#6B7280" fontSize={12} />
                        <YAxis stroke="#6B7280" fontSize={12} />
                        <Tooltip 
                          contentStyle={{ 
                            backgroundColor: 'white', 
                            border: '1px solid #e5e7eb', 
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                            direction: 'ltr'
                          }} 
                          labelFormatter={(value) => `تاریخ: ${value}`}
                          formatter={(value, name, props) => [
                            typeof value === 'number' ? value.toLocaleString('fa-IR') : value, 
                            props.dataKey === 'views' ? 'آگهی‌های مشاهده شده توسط شما' : 'علاقه‌مندی‌ها برای آگهی‌های شما'
                          ]}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="views" 
                          stroke="#3B82F6" 
                          strokeWidth={2}
                          fill="url(#viewsGradient)"
                          name="بازدیدها"
                        />
                        <Area 
                          type="monotone" 
                          dataKey="favorites" 
                          stroke="#10B981" 
                          strokeWidth={2}
                          fill="url(#favoritesGradient)"
                          name="علاقه‌مندی‌ها"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Category Distribution Chart */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">آگهی‌ها بر اساس دسته‌بندی</h3>
                    {chartData.categoryStats && chartData.categoryStats.length > 0 ? (
                      <>
                        <div className="flex items-center justify-center">
                          <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                              <Pie
                                data={chartData.categoryStats}
                                cx="50%"
                                cy="50%"
                                innerRadius={60}
                                outerRadius={100}
                                paddingAngle={5}
                                dataKey="adCount"
                              >
                                {chartData.categoryStats.map((entry: any, index: number) => (
                                  <Cell key={`cell-${index}`} fill={`hsl(${index * 45}, 70%, 60%)`} />
                                ))}
                              </Pie>
                              <Tooltip 
                                formatter={(value: any, name: string) => [value, 'آگهی']}
                                labelFormatter={(label) => `دسته‌بندی: ${label}`}
                                contentStyle={{ 
                                  backgroundColor: 'white', 
                                  border: '1px solid #e5e7eb', 
                                  borderRadius: '8px',
                                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                  direction: 'ltr'
                                }} 
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="mt-4 space-y-2">
                          {chartData.categoryStats.map((cat: any, index: number) => (
                            <div key={index} className="flex items-center justify-between">
                              <div className="flex items-center">
                                <div 
                                  className="w-3 h-3 rounded-full ml-3" 
                                  style={{ backgroundColor: `hsl(${index * 45}, 70%, 60%)` }}
                                ></div>
                                <span className="text-sm text-gray-700 font-medium">{cat.categoryName}</span>
                              </div>
                              <div className="text-left">
                                <span className="text-sm font-semibold text-gray-900">{cat.adCount}</span>
                                <span className="text-xs text-gray-500 mr-2">آگهی</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="flex items-center justify-center py-8">
                        <div className="text-center">
                          <div className="text-gray-400 mb-2">📊</div>
                          <p className="text-gray-500">هنوز آگهی‌ای ثبت نشده است</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Plan Features Usage Chart */}
                  <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">استفاده از ویژگی‌های پلن</h3>
                      {userPlans?.data && userPlans.data.length > 1 && (
                        <select
                          value={selectedPlanId}
                          onChange={(e) => setSelectedPlanId(e.target.value)}
                          className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                        >
                          {userPlans.data.map((plan) => (
                            <option key={plan.id} value={plan.id}>
                              {plan.name} {plan.isActive && !plan.isExpired ? '(فعال)' : plan.isExpired ? '(منقضی شده)' : ''}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                    {userPlans?.data && userPlans.data.length > 0 && selectedPlanId && userPlans.data.find(plan => plan.id === selectedPlanId) ? (
                      <div className="space-y-4">
                        {/* Plan Info */}
                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                          <div className="flex items-center justify-between">
                            <h4 className="font-semibold text-gray-900">{userPlans.data.find(plan => plan.id === selectedPlanId)!.name}</h4>
                            <div className="text-sm text-gray-600">
                              تاریخ انقضا: {new Date(userPlans.data.find(plan => plan.id === selectedPlanId)!.expirationDate).toLocaleDateString('fa-IR')}
                            </div>
                          </div>
                        </div>
                      
                        {/* Beautiful Progress Bars */}
                        <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 rounded-2xl p-6 border border-slate-200/60 shadow-xl">
                          <div className="space-y-6">
                            {[
                              { 
                                name: 'آگهی برتر', 
                                used: userPlans.data.find(plan => plan.id === selectedPlanId)!.usedTopAdCount, 
                                remaining: userPlans.data.find(plan => plan.id === selectedPlanId)!.remainingTopAdCount,
                                total: userPlans.data.find(plan => plan.id === selectedPlanId)!.usedTopAdCount + userPlans.data.find(plan => plan.id === selectedPlanId)!.remainingTopAdCount,
                                icon: '🔝',
                                color: 'from-red-500 to-red-600'
                              },
                              { 
                                name: 'برجسته', 
                                used: userPlans.data.find(plan => plan.id === selectedPlanId)!.usedHighlightCount, 
                                remaining: userPlans.data.find(plan => plan.id === selectedPlanId)!.remainingHighlightCount,
                                total: userPlans.data.find(plan => plan.id === selectedPlanId)!.usedHighlightCount + userPlans.data.find(plan => plan.id === selectedPlanId)!.remainingHighlightCount,
                                icon: '✨',
                                color: 'from-orange-500 to-orange-600'
                              },
                              { 
                                name: 'برچسب فوری', 
                                used: userPlans.data.find(plan => plan.id === selectedPlanId)!.usedUrgentTagCount, 
                                remaining: userPlans.data.find(plan => plan.id === selectedPlanId)!.remainingUrgentTagCount,
                                total: userPlans.data.find(plan => plan.id === selectedPlanId)!.usedUrgentTagCount + userPlans.data.find(plan => plan.id === selectedPlanId)!.remainingUrgentTagCount,
                                icon: '⚡',
                                color: 'from-yellow-500 to-yellow-600'
                              },
                              { 
                                name: 'افزایش رتبه', 
                                used: userPlans.data.find(plan => plan.id === selectedPlanId)!.usedBumpUpCount, 
                                remaining: userPlans.data.find(plan => plan.id === selectedPlanId)!.remainingBumpUpCount,
                                total: userPlans.data.find(plan => plan.id === selectedPlanId)!.usedBumpUpCount + userPlans.data.find(plan => plan.id === selectedPlanId)!.remainingBumpUpCount,
                                icon: '⬆️',
                                color: 'from-blue-500 to-blue-600'
                              },
                              { 
                                name: 'گالری صفحه اصلی', 
                                used: userPlans.data.find(plan => plan.id === selectedPlanId)!.usedHomepageGalleryCount, 
                                remaining: userPlans.data.find(plan => plan.id === selectedPlanId)!.remainingHomepageGalleryCount,
                                total: userPlans.data.find(plan => plan.id === selectedPlanId)!.usedHomepageGalleryCount + userPlans.data.find(plan => plan.id === selectedPlanId)!.remainingHomepageGalleryCount,
                                icon: '🏠',
                                color: 'from-purple-500 to-purple-600'
                              }
                            ].filter(feature => feature.total > 0).map((feature, index) => {
                              const usedPercentage = feature.total > 0 ? (feature.used / feature.total) * 100 : 0;
                              const remainingPercentage = 100 - usedPercentage;
                              
                              return (
                                <div key={feature.name} className="bg-white/70 backdrop-blur-sm rounded-xl p-4 shadow-md border border-white/50 hover:shadow-lg transition-all duration-300">
                                  {/* Header */}
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center space-x-3">
                                      <div className="text-2xl">{feature.icon}</div>
                                      <div>
                                        <h4 className="font-semibold text-gray-900 text-lg">{feature.name}</h4>
                                        <p className="text-sm text-gray-600">{feature.used} used of {feature.total} total</p>
                                      </div>
                                    </div>
                                    <div className="text-left">
                                      <div className="text-2xl font-semibold text-gray-900">{Math.round(usedPercentage)}%</div>
                                      <div className="text-sm text-gray-500">Used</div>
                                    </div>
                                  </div>
                                  
                                  {/* Progress Bar */}
                                  <div className="relative">
                                    <div className="h-4 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                                      <div 
                                        className={`h-full bg-gradient-to-r ${feature.color} transition-all duration-1000 ease-out relative overflow-hidden`}
                                        style={{ width: `${usedPercentage}%` }}
                                      >
                                        {/* Animated shine effect */}
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 animate-pulse"></div>
                                      </div>
                                    </div>
                                    
                                    {/* Progress Labels */}
                                    <div className="flex justify-between mt-2 text-xs font-medium">
                                      <span className="text-red-600">Used: {feature.used}</span>
                                      <span className="text-green-600">باقی‌مانده: {feature.remaining}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          
                          {/* Beautiful Legend */}
                          <div className="flex items-center justify-center mt-6 space-x-8">
                            <div className="flex items-center space-x-2">
                              <div className="w-4 h-4 rounded-full bg-gradient-to-r from-red-500 to-red-600 shadow-lg"></div>
                              <span className="text-sm font-semibold text-slate-700">Used</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="w-4 h-4 rounded-full bg-gray-200 shadow-lg"></div>
                              <span className="text-sm font-semibold text-slate-700">باقی‌مانده</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <div className="text-gray-400 mb-2">📋</div>
                        <p className="text-gray-500">جزئیات پلن در دسترس نیست</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Listings Tab */}
            {activeTab === 'listings' && (
              <div className="space-y-8">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h1 className="text-3xl font-semibold text-gray-900 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text">آگهی‌های من</h1>
                    <Link
                      to="/post-ad"
                      className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-6 py-2.5 rounded-xl font-medium transition-all duration-200 flex items-center shadow-lg hover:shadow-red-200 transform hover:scale-105"
                    >
                      <PencilIcon className="h-5 w-5 ml-2" />
                      ایجاد آگهی جدید
                    </Link>
                  </div>

                  {/* Search and Filter Bar */}
                  <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                    <div className="flex flex-col lg:flex-row gap-4">
                      {/* Search Input */}
                      <div className="lg:w-64">
                        <div className="relative">
                          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                          <input
                            type="text"
                            placeholder="جستجو در آگهی‌های من..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200"
                            dir="ltr"
                          />
                        </div>
                      </div>
                      
                      {/* Filter Options */}
                      <div className="flex flex-wrap gap-3 lg:flex-1">
                        <select 
                          value={statusFilter}
                          onChange={(e) => setStatusFilter(e.target.value)}
                          className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 bg-white text-gray-700 appearance-none bg-no-repeat bg-right pr-10 text-left lg:min-w-[140px]"
                          style={{
                            backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                            backgroundPosition: 'right 0.75rem center',
                            backgroundSize: '1.5em 1.5em'
                          }}
                        >
                          {statusOptions.map((status) => (
                            <option key={status.value} value={status.value}>
                              {status.label}
                            </option>
                          ))}
                        </select>
                        
                                                 <div className="relative category-dropdown">
                           <button
                             type="button"
                             onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                             className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-200 bg-white text-gray-700 text-left flex items-center justify-between lg:min-w-[240px]"
                           >
                            <span>{selectedCategoryName}</span>
                            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 20 20" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 8l4 4 4-4" />
                            </svg>
                          </button>
                          
                          {categoryDropdownOpen && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-hidden">
                              <div className="p-3 border-b border-gray-100">
                                <input
                                  type="text"
                                  placeholder="جستجو در دسته‌بندی‌ها..."
                                  value={categorySearchQuery}
                                  onChange={(e) => setCategorySearchQuery(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent text-left"
                                />
                              </div>
                              <div className="max-h-40 overflow-y-auto">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCategoryFilter('');
                                    setCategoryDropdownOpen(false);
                                    setCategorySearchQuery('');
                                  }}
                                  className="w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors"
                                >
                                  همه دسته‌بندی‌ها
                                </button>
                                {filteredCategories.map((category) => (
                                  <button
                                    key={category.id}
                                    type="button"
                                    onClick={() => {
                                      setCategoryFilter(category.id);
                                      setCategoryDropdownOpen(false);
                                      setCategorySearchQuery('');
                                    }}
                                    className="w-full px-4 py-2 text-left hover:bg-gray-50 transition-colors"
                                  >
                                    {category.name}
                                  </button>
                                ))}
                                {filteredCategories.length === 0 && categorySearchQuery && (
                                  <div className="px-4 py-2 text-gray-500 text-left">
                                    دسته‌بندی یافت نشد
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                        
                        <button 
                          onClick={() => setShowAdvancedFilters(true)}
                          className={`px-4 py-3 rounded-xl transition-all duration-200 flex items-center gap-2 relative ${
                            hasActiveAdvancedFilters() 
                              ? 'bg-primary-100 hover:bg-primary-200 text-primary-700 border border-primary-300' 
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          فیلترهای بیشتر
                          <FunnelIcon className="h-5 w-5" />
                          {hasActiveAdvancedFilters() && (
                            <span className="absolute -top-1 -right-1 h-3 w-3 bg-primary-500 rounded-full"></span>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {userAds?.data && userAds.data.length > 0 ? (
                  <div className="space-y-6">
                    {/* Results Summary */}
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-blue-700 font-medium">
                          نمایش {paginatedAds.length} آگهی از {filteredAds.length} آگهی کل
                          {filteredAds.length !== userAds.data.length && ` (فیلتر شده از ${userAds.data.length})`}
                        </span>
                        <div className="flex items-center gap-4 text-blue-600">
                          <span className="flex items-center gap-1">
                            <EyeIcon className="h-4 w-4" />
                            {realUserStats.totalViews.toLocaleString('fa-IR')} بازدید
                          </span>
                          <span className="flex items-center gap-1">
                            <HeartIcon className="h-4 w-4" />
                            {realUserStats.totalFavorites} علاقه‌مندی
                          </span>
                        </div>
                      </div>
                    </div>

                    {paginatedAds.length > 0 ? (
                      <>
                        <div className="relative">
                          {isChangingPage && (
                            <TimchehLoader 
                              fullScreen={false}
                              size="medium" 
                              message="در حال بارگذاری صفحه..." 
                              className="absolute inset-0 bg-white bg-opacity-90 z-10"
                            />
                          )}
                          <div className="space-y-3">
                            {paginatedAds.map((ad) => (
                            <div key={ad.id} className="bg-white rounded-lg p-3 lg:p-4 shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200">
                              {/* Mobile Layout */}
                              <div className="block lg:hidden">
                                {/* Top Row - Image, Title, Price */}
                                <div className="flex items-start gap-3 mb-3">
                                  {/* Image */}
                                  <div className="flex-shrink-0">
                                    {ad.primaryImageUrl ? (
                                      <img 
                                        src={ad.primaryImageUrl} 
                                        alt={ad.title}
                                        className="w-16 h-16 rounded-lg object-cover"
                                      />
                                    ) : (
                                      <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                                        <PhotoIcon className="h-8 w-8 text-gray-400" />
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Title and Price */}
                                  <div className="flex-1 min-w-0">
                                    <h3 className="text-base font-semibold text-gray-900 mb-1 truncate">{ad.title}</h3>
                                    <p className="text-sm text-gray-500 mb-2">{ad.categoryName}</p>
                                    <div className="text-lg font-semibold text-green-600">
                                      CAD ${ad.price.toLocaleString('en-CA')}
                                    </div>
                                  </div>
                                  
                                  {/* Status Badge */}
                                  <div className="flex-shrink-0">
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                      ad.status === 'Active' ? 'bg-green-100 text-green-800' :
                                      ad.status === 'Sold' ? 'bg-blue-100 text-blue-800' :
                                      ad.status === 'PendingApproval' ? 'bg-yellow-100 text-yellow-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {ad.status === 'Active' ? 'فعال' : 
                                       ad.status === 'Sold' ? 'Sold' : 
                                       ad.status === 'PendingApproval' ? 'Pending Approval' : 
                                       ad.status || 'Active'}
                                    </span>
                                  </div>
                                </div>

                                {/* Middle Row - Date, Location, Stats */}
                                <div className="flex items-center justify-between mb-3 text-sm text-gray-500">
                                  <div className="flex items-center gap-4">
                                    <div className="flex items-center">
                                      <CalendarIcon className="h-4 w-4 mr-1" />
                                      {new Date(ad.datePosted).toLocaleDateString('fa-IR')}
                                    </div>
                                    <div className="flex items-center">
                                      <MapPinIcon className="h-4 w-4 mr-1" />
                                      {ad.city}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <div className="flex items-center">
                                      <EyeIcon className="h-4 w-4 mr-1" />
                                      {ad.viewCount || 0}
                                    </div>
                                    <div className="flex items-center">
                                      <HeartIcon className="h-4 w-4 mr-1" />
                                      {ad.favoriteCount || 0}
                                    </div>
                                  </div>
                                </div>

                                {/* Bottom Row - Action Buttons */}
                                <div className="flex items-center justify-between">
                                  {/* Primary Actions */}
                                  <div className="flex space-x-2">
                                    <Link
                                      to={`/ads/${ad.slug || ad.id}`}
                                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                    >
                                      View
                                    </Link>
                                    <Link
                                      to={`/edit-ad/${ad.id}`}
                                      className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                    >
                                      Edit
                                    </Link>
                                  </div>

                                  {/* Secondary Actions */}
                                  <div className="flex space-x-1">
                                    <button
                                      className={`p-1.5 rounded-lg transition-colors ${
                                        ad.isTopAd 
                                          ? 'text-yellow-600 bg-yellow-50 cursor-not-allowed' 
                                          : 'text-yellow-600 hover:bg-yellow-50'
                                      }`}
                                      title={ad.isTopAd ? 'آگهی برتر اعمال شده' : 'اعمال آگهی برتر'}
                                      onClick={() => !ad.isTopAd && handleApplyTopAd(ad.id)}
                                      disabled={ad.isTopAd || applyingPromotions.has(`${ad.id}-top`)}
                                    >
                                      {applyingPromotions.has(`${ad.id}-top`) ? (
                                        <div className="animate-spin rounded-full h-3 w-3 border-2 border-yellow-600 border-top-transparent"></div>
                                      ) : (
                                        <StarIcon className="h-3 w-3" />
                                      )}
                                    </button>
                                    <button
                                      className={`p-1.5 rounded-lg transition-colors ${
                                        ad.isHighlighted 
                                          ? 'text-orange-600 bg-orange-50 cursor-not-allowed' 
                                          : 'text-orange-600 hover:bg-orange-50'
                                      }`}
                                      title={ad.isHighlighted ? 'برجسته اعمال شده' : 'اعمال برجسته'}
                                      onClick={() => !ad.isHighlighted && handleApplyHighlight(ad.id)}
                                      disabled={ad.isHighlighted || applyingPromotions.has(`${ad.id}-highlight`)}
                                    >
                                      {applyingPromotions.has(`${ad.id}-highlight`) ? (
                                        <div className="animate-spin rounded-full h-3 w-3 border-2 border-orange-600 border-top-transparent"></div>
                                      ) : (
                                        <SparklesIcon className="h-3 w-3" />
                                      )}
                                    </button>
                                    <button
                                      className={`p-1.5 rounded-lg transition-colors ${
                                        ad.isUrgent 
                                          ? 'text-red-600 bg-red-50 cursor-not-allowed' 
                                          : 'text-red-600 hover:bg-red-50'
                                      }`}
                                      title={ad.isUrgent ? 'برچسب فوری اعمال شده' : 'اعمال برچسب فوری'}
                                      onClick={() => !ad.isUrgent && handleApplyUrgentTag(ad.id)}
                                      disabled={ad.isUrgent || applyingPromotions.has(`${ad.id}-urgent`)}
                                    >
                                      {applyingPromotions.has(`${ad.id}-urgent`) ? (
                                        <div className="animate-spin rounded-full h-3 w-3 border-2 border-red-600 border-top-transparent"></div>
                                      ) : (
                                        <BoltIcon className="h-3 w-3" />
                                      )}
                                    </button>
                                    <button
                                      className="p-1.5 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                      title="افزایش رتبه آگهی"
                                      onClick={() => handleBumpUpAd(ad.id)}
                                      disabled={boostingAds.has(ad.id)}
                                    >
                                      {boostingAds.has(ad.id) ? (
                                        <div className="animate-spin rounded-full h-3 w-3 border-2 border-purple-600 border-top-transparent"></div>
                                      ) : (
                                        <ArrowTrendingUpIcon className="h-3 w-3" />
                                      )}
                                    </button>
                                    <button
                                      className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                      title={ad.status === 'Sold' ? 'Sold' : 'Mark as Sold'}
                                      onClick={() => handleMarkAdAsSold(ad.id)}
                                      disabled={markingSoldAds.has(ad.id) || ad.status === 'Sold'}
                                    >
                                      {markingSoldAds.has(ad.id) ? (
                                        <div className="animate-spin rounded-full h-3 w-3 border-2 border-green-600 border-top-transparent"></div>
                                      ) : (
                                        <CheckBadgeIcon className="h-3 w-3" />
                                      )}
                                    </button>
                                    <button
                                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Delete"
                                      onClick={() => {
                                        showModal({
                                          title: 'حذف آگهی',
                                          message: `آیا مطمئن هستید که می‌خواهید آگهی "${ad.title}" را حذف کنید؟\nاین عمل غیرقابل بازگشت است.`,
                                          confirmText: 'حذف',
                                          cancelText: 'لغو',
                                          type: 'error',
                                          isRtl: true,
                                          onConfirm: () => {
                                            deleteAdMutation.mutate(ad.id);
                                          }
                                        });
                                      }}
                                    >
                                      <TrashIcon className="h-3 w-3" />
                                    </button>
                                  </div>
                                </div>
                              </div>

                              {/* Desktop Layout */}
                              <div className="hidden lg:flex items-center justify-between">
                                {/* Left side - Image */}
                                <div className="flex-shrink-0">
                                  {ad.primaryImageUrl ? (
                                    <img 
                                      src={ad.primaryImageUrl} 
                                      alt={ad.title}
                                      className="w-16 h-16 rounded-lg object-cover"
                                    />
                                  ) : (
                                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                                      <PhotoIcon className="h-8 w-8 text-gray-400" />
                                    </div>
                                  )}
                                </div>

                                {/* Center - Ad Info */}
                                <div className="flex-1 mx-6">
                                  <div className="flex items-center justify-between">
                                    <div>
                                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{ad.title}</h3>
                                      <p className="text-sm text-gray-500">{ad.categoryName}</p>
                                    </div>
                                    <div className="text-left">
                                      <div className="text-xl font-semibold text-green-600 mb-1">
                                        CAD ${ad.price.toLocaleString('en-CA')}
                                      </div>
                                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                                        ad.status === 'Active' ? 'bg-green-100 text-green-800' :
                                        ad.status === 'Sold' ? 'bg-blue-100 text-blue-800' :
                                        ad.status === 'PendingApproval' ? 'bg-yellow-100 text-yellow-800' :
                                        'bg-gray-100 text-gray-800'
                                      }`}>
                                        {ad.status === 'Active' ? 'فعال' : 
                                         ad.status === 'Sold' ? 'Sold' : 
                                         ad.status === 'PendingApproval' ? 'Pending Approval' : 
                                         ad.status || 'Active'}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between mt-2 text-sm text-gray-500">
                                    <div className="flex items-center">
                                      <CalendarIcon className="h-4 w-4 mr-1" />
                                      {new Date(ad.datePosted).toLocaleDateString('fa-IR')}
                                    </div>
                                    <div className="flex items-center">
                                      <MapPinIcon className="h-4 w-4 mr-1" />
                                      {ad.city}, {ad.province}
                                    </div>
                                  </div>
                                </div>

                                {/* Right side - Action buttons and stats */}
                                <div className="flex items-center space-x-4">
                                  <div className="flex space-x-1">
                                    <button
                                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                      title="Delete"
                                      onClick={() => {
                                        showModal({
                                          title: 'حذف آگهی',
                                          message: `آیا مطمئن هستید که می‌خواهید آگهی "${ad.title}" را حذف کنید؟\nاین عمل غیرقابل بازگشت است.`,
                                          confirmText: 'حذف',
                                          cancelText: 'لغو',
                                          type: 'error',
                                          isRtl: true,
                                          onConfirm: () => {
                                            deleteAdMutation.mutate(ad.id);
                                          }
                                        });
                                      }}
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                    <button
                                      className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                                      title="افزایش رتبه آگهی"
                                      onClick={() => handleBumpUpAd(ad.id)}
                                      disabled={boostingAds.has(ad.id)}
                                    >
                                      {boostingAds.has(ad.id) ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-purple-600 border-top-transparent"></div>
                                      ) : (
                                        <ArrowTrendingUpIcon className="h-4 w-4" />
                                      )}
                                    </button>
                                    {/* Top Ad - Top Ad */}
                                    <button
                                      className={`p-2 rounded-lg transition-colors ${
                                        ad.isTopAd 
                                          ? 'text-yellow-600 bg-yellow-50 cursor-not-allowed' 
                                          : 'text-yellow-600 hover:bg-yellow-50'
                                      }`}
                                      title={ad.isTopAd ? 'آگهی برتر اعمال شده' : 'اعمال آگهی برتر'}
                                      onClick={() => !ad.isTopAd && handleApplyTopAd(ad.id)}
                                      disabled={ad.isTopAd || applyingPromotions.has(`${ad.id}-top`)}
                                    >
                                      {applyingPromotions.has(`${ad.id}-top`) ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-yellow-600 border-top-transparent"></div>
                                      ) : (
                                        <StarIcon className="h-4 w-4" />
                                      )}
                                    </button>
                                    {/* Highlight - Highlight */}
                                    <button
                                      className={`p-2 rounded-lg transition-colors ${
                                        ad.isHighlighted 
                                          ? 'text-orange-600 bg-orange-50 cursor-not-allowed' 
                                          : 'text-orange-600 hover:bg-orange-50'
                                      }`}
                                      title={ad.isHighlighted ? 'برجسته اعمال شده' : 'اعمال برجسته'}
                                      onClick={() => !ad.isHighlighted && handleApplyHighlight(ad.id)}
                                      disabled={ad.isHighlighted || applyingPromotions.has(`${ad.id}-highlight`)}
                                    >
                                      {applyingPromotions.has(`${ad.id}-highlight`) ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-600 border-top-transparent"></div>
                                      ) : (
                                        <SparklesIcon className="h-4 w-4" />
                                      )}
                                    </button>
                                    {/* Urgent Tag - Urgent Tag */}
                                    <button
                                      className={`p-2 rounded-lg transition-colors ${
                                        ad.isUrgent 
                                          ? 'text-red-600 bg-red-50 cursor-not-allowed' 
                                          : 'text-red-600 hover:bg-red-50'
                                      }`}
                                      title={ad.isUrgent ? 'برچسب فوری اعمال شده' : 'اعمال برچسب فوری'}
                                      onClick={() => !ad.isUrgent && handleApplyUrgentTag(ad.id)}
                                      disabled={ad.isUrgent || applyingPromotions.has(`${ad.id}-urgent`)}
                                    >
                                      {applyingPromotions.has(`${ad.id}-urgent`) ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-red-600 border-top-transparent"></div>
                                      ) : (
                                        <BoltIcon className="h-4 w-4" />
                                      )}
                                    </button>
                                    <button
                                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                      title={ad.status === 'Sold' ? 'Sold' : 'Mark as Sold'}
                                      onClick={() => handleMarkAdAsSold(ad.id)}
                                      disabled={markingSoldAds.has(ad.id) || ad.status === 'Sold'}
                                    >
                                      {markingSoldAds.has(ad.id) ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-green-600 border-top-transparent"></div>
                                      ) : (
                                        <CheckBadgeIcon className="h-4 w-4" />
                                      )}
                                    </button>
                                    <Link
                                      to={`/edit-ad/${ad.id}`}
                                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                      title="Edit"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </Link>
                                    <Link
                                      to={`/ads/${ad.slug || ad.id}`}
                                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                      title="View"
                                    >
                                      <EyeIcon className="h-4 w-4" />
                                    </Link>
                                  </div>

                                  {/* Stats */}
                                  <div className="flex items-center space-x-4 text-sm text-gray-500">
                                    <div className="flex items-center">
                                      <EyeIcon className="h-4 w-4 mr-1" />
                                      {ad.viewCount || 0}
                                    </div>
                                    <div className="flex items-center">
                                      <HeartIcon className="h-4 w-4 mr-1" />
                                      {ad.favoriteCount || 0}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                          </div>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <div className="flex items-center justify-center gap-2 mt-8">
                            <button
                              onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                              disabled={currentPage === 1 || isChangingPage}
                              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              قبلی
                            </button>
                            
                            {[...Array(totalPages)].map((_, index) => {
                              const pageNumber = index + 1;
                              if (
                                pageNumber === 1 ||
                                pageNumber === totalPages ||
                                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                              ) {
                                return (
                                  <button
                                    key={pageNumber}
                                    onClick={() => handlePageChange(pageNumber)}
                                    disabled={isChangingPage}
                                    className={`px-3 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                                      currentPage === pageNumber
                                        ? 'bg-primary-600 text-white'
                                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                                  >
                                    {pageNumber}
                                  </button>
                                );
                              } else if (
                                pageNumber === currentPage - 2 ||
                                pageNumber === currentPage + 2
                              ) {
                                return <span key={pageNumber}>...</span>;
                              }
                              return null;
                            })}
                            
                            <button
                              onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
                              disabled={currentPage === totalPages || isChangingPage}
                              className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              بعدی
                            </button>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200">
                        <ListBulletIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">No ads found</h3>
                        <p className="text-gray-500 mb-6">Try again with different filters or a new search.</p>
                        <button
                          onClick={() => {
                            setSearchQuery('');
                            setStatusFilter('');
                            setCategoryFilter('');
                          }}
                          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                        >
                          Clear Filters
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200">
                    <ListBulletIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">شما هنوز آگهی ثبت نکرده‌اید</h3>
                    <p className="text-gray-500 mb-6">با ایجاد اولین آگهی خود شروع کنید تا خریداران بالقوه را جذب کنید.</p>
                    <Link
                      to="/post-ad"
                      className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center"
                    >
                      <PencilIcon className="h-5 w-5 ml-2" />
                      ایجاد اولین آگهی شما
                    </Link>
                  </div>
                )}
              </div>
            )}



            {/* Favorites Tab */}
            {activeTab === 'favorites' && (
              <div className="space-y-6 lg:space-y-8">
                {/* Header - Responsive */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <h1 className="text-2xl lg:text-3xl font-semibold text-gray-900">علاقه‌مندی‌ها</h1>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <HeartIcon className="h-4 w-4 lg:h-5 lg:w-5" />
                    <span className="text-xs lg:text-sm">آگهی‌هایی که علاقه‌مند شده‌اید</span>
                  </div>
                </div>

                {isLoadingFavorites ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden animate-pulse">
                        <div className="w-full h-40 sm:h-48 bg-gray-200"></div>
                        <div className="p-3 lg:p-4 space-y-3">
                          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-6 bg-gray-200 rounded w-1/2"></div>
                          <div className="flex justify-between">
                            <div className="h-3 bg-gray-200 rounded w-1/3"></div>
                            <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (favoriteAds?.data?.data && Array.isArray(favoriteAds.data.data) && favoriteAds.data.data.length > 0 && favoriteAds.data.totalCount > 0) ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
                    {favoriteAds.data.data.map((ad: AdListItem) => (
                      <div key={ad.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300 group">
                        <Link to={`/ads/${ad.slug || ad.id}`} className="block">
                          <div className="relative">
                            <img
                              src={ad.primaryImageUrl || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+CiAgPHRleHQgeD0iNTAlIiB5PSI1MCUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5Y2EzYWYiIGZvbnQtZmFtaWx5PSJtb25vc3BhY2UiIGZvbnQtc2l6ZT0iMTNweCI+Tm8gSW1hZ2U8L3RleHQ+Cjwvc3ZnPg=='}
                              alt={ad.title}
                              className="w-full h-40 sm:h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                            
                            {/* Badges - Mobile Optimized */}
                            <div className="absolute top-2 right-2 flex flex-col gap-1">
                              {ad.isUrgent && (
                                <span className="bg-red-500 text-white text-xs font-medium px-1.5 py-0.5 sm:px-2 sm:py-1 rounded shadow-sm">
                                  فوری
                                </span>
                              )}
                              {ad.isHighlighted && (
                                <span className="bg-orange-500 text-white text-xs font-medium px-1.5 py-0.5 sm:px-2 sm:py-1 rounded shadow-sm">
                                  برجسته
                                </span>
                              )}
                              {ad.isTopAd && (
                                <span className="bg-purple-500 text-white text-xs font-medium px-1.5 py-0.5 sm:px-2 sm:py-1 rounded shadow-sm">
                                  آگهی برتر
                                </span>
                              )}
                              {ad.isFeatured && (
                                <span className="bg-primary-600 text-white text-xs font-medium px-1.5 py-0.5 sm:px-2 sm:py-1 rounded shadow-sm">
                                  ویژه
                                </span>
                              )}
                            </div>
                            
                            {/* Favorite Button - Mobile Optimized */}
                            <button
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                try {
                                  await adsApi.toggleFavorite(ad.id);
                                  queryClient.invalidateQueries({ queryKey: ['favorites', favoritesPage, favoritesPageSize] });
                                  toast.success('از علاقه‌مندی‌ها حذف شد', { style: { direction: 'rtl', textAlign: 'right' } });
                                } catch (error) {
                                  toast.error('خطا در حذف از علاقه‌مندی‌ها', { style: { direction: 'rtl', textAlign: 'right' } });
                                }
                              }}
                              className="absolute top-2 left-2 p-1.5 sm:p-2 bg-white/90 rounded-full hover:bg-white transition-colors shadow-sm"
                            >
                              <HeartIcon className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 fill-current" />
                            </button>
                          </div>
                          
                          {/* Card Content - Mobile Optimized */}
                          <div className="p-3 lg:p-4">
                            <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-2 sm:mb-3 line-clamp-2 hover:text-primary-600 transition-colors leading-tight">
                              {ad.title}
                            </h3>
                            <div className="text-lg sm:text-xl font-semibold text-green-600 mb-2 sm:mb-3">
                              CAD ${ad.price.toLocaleString('en-CA')}
                            </div>
                            
                            {/* Location and Date - Mobile Stack */}
                            <div className="space-y-1 sm:space-y-0 sm:flex sm:items-center sm:justify-between text-xs sm:text-sm text-gray-500">
                              <div className="flex items-center text-gray-600">
                                <MapPinIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 text-primary-600 flex-shrink-0" />
                                <span className="truncate">{ad.city}, {ad.province}</span>
                              </div>
                              <div className="flex items-center text-gray-600">
                                <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 text-primary-600 flex-shrink-0" />
                                <span className="whitespace-nowrap">{new Date(ad.datePosted).toLocaleDateString('en-US')}</span>
                              </div>
                            </div>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200">
                    <HeartIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">هنوز علاقه‌مندی‌ای وجود ندارد</h3>
                    <p className="text-gray-500 mb-6">شما هنوز آگهی‌ای را علاقه‌مند نکرده‌اید.</p>
                    <Link
                      to="/search"
                      className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center"
                    >
                      <MagnifyingGlassIcon className="h-5 w-5 ml-2" />
                      جستجوی آگهی‌ها
                    </Link>
                  </div>
                )}

                {/* Pagination for favorites - Mobile Responsive */}
                {favoriteAds?.data?.totalPages !== undefined && favoriteAds.data.totalPages !== null && typeof favoriteAds.data.totalPages === 'number' && favoriteAds.data.totalPages > 1 && favoriteAds?.data?.totalCount !== undefined && favoriteAds.data.totalCount !== null && typeof favoriteAds.data.totalCount === 'number' && favoriteAds.data.totalCount > 0 && (
                  <div className="mt-6 lg:mt-8">
                    {/* Mobile Pagination */}
                    <div className="flex sm:hidden items-center justify-between">
                      <button
                        onClick={() => setFavoritesPage(Math.max(1, favoritesPage - 1))}
                        disabled={favoritesPage === 1}
                        className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed disabled:bg-gray-100"
                      >
                        قبلی
                      </button>
                      

                      {favoriteAds.data.totalCount > 0 && favoriteAds.data.totalPages > 0 && (
                        <div className="text-sm text-gray-600 font-medium">
                          صفحه {favoritesPage} از {favoriteAds.data.totalPages}
                        </div>
                      )}
                      
                      <button
                        onClick={() => setFavoritesPage(Math.min(favoriteAds.data.totalPages, favoritesPage + 1))}
                        disabled={favoritesPage === favoriteAds.data.totalPages}
                        className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed disabled:bg-gray-100"
                      >
                        بعدی
                      </button>
                    </div>

                    {/* Desktop Pagination */}
                    <div className="hidden sm:flex items-center justify-center space-x-2">
                      <button
                        onClick={() => setFavoritesPage(Math.max(1, favoritesPage - 1))}
                        disabled={favoritesPage === 1}
                        className="px-3 py-1 text-sm text-gray-500 hover:text-primary-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                      >
                        قبلی
                      </button>
                      
                      {[...Array(Math.min(favoriteAds.data.totalPages, 7))].map((_, index) => {
                        let page: number;
                        if (favoriteAds.data.totalPages <= 7) {
                          page = index + 1;
                        } else {
                          if (favoritesPage <= 4) {
                            page = index + 1;
                          } else if (favoritesPage >= favoriteAds.data.totalPages - 3) {
                            page = favoriteAds.data.totalPages - 6 + index;
                          } else {
                            page = favoritesPage - 3 + index;
                          }
                        }
                        
                        return (
                          <button
                            key={page}
                            onClick={() => setFavoritesPage(page)}
                            className={`px-3 py-1 text-sm rounded ${
                              page === favoritesPage 
                                ? 'bg-primary-600 text-white' 
                                : 'text-gray-600 hover:text-primary-600 hover:bg-primary-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => setFavoritesPage(Math.min(favoriteAds.data.totalPages, favoritesPage + 1))}
                        disabled={favoritesPage === favoriteAds.data.totalPages}
                        className="px-3 py-1 text-sm text-gray-500 hover:text-primary-600 disabled:text-gray-300 disabled:cursor-not-allowed"
                      >
                        بعدی
                      </button>
                      
                      {favoriteAds.data.totalCount > 0 && favoriteAds.data.totalPages > 0 && (
                        <div className="text-sm text-gray-500 mr-4">
                          صفحه {favoritesPage} از {favoriteAds.data.totalPages} 
                          ({favoriteAds.data.totalCount} مورد)
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Payment Details Tab */}
            {activeTab === 'payments' && (
              <div className="space-y-8">
                <div className="flex items-center justify-between">
                  <h1 className="text-3xl font-semibold text-gray-900">جزئیات پرداخت</h1>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <CreditCardIcon className="h-5 w-5" />
                    <span>تاریخچه تراکنش‌ها</span>
                  </div>
                </div>

                {isLoadingPayments ? (
                  <div className="space-y-4">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 animate-pulse">
                        <div className="flex items-center justify-between">
                          <div className="space-y-2">
                            <div className="h-4 bg-gray-200 rounded w-32"></div>
                            <div className="h-3 bg-gray-200 rounded w-24"></div>
                          </div>
                          <div className="h-6 bg-gray-200 rounded w-20"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : userPayments?.data?.data && userPayments.data.data.length > 0 ? (
                  <div className="space-y-4">
                    {userPayments.data.data.map((payment) => (
                      <div key={payment.id} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-full ${
                              payment.status === 'Completed' ? 'bg-green-100' :
                              payment.status === 'Failed' ? 'bg-red-100' :
                              'bg-yellow-100'
                            }`}>
                              {payment.status === 'Completed' ? (
                                <CheckCircleIcon className="h-6 w-6 text-green-600" />
                              ) : payment.status === 'Failed' ? (
                                <XCircleIcon className="h-6 w-6 text-red-600" />
                              ) : (
                                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
                              )}
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">
                                خرید پکیج پریمیوم
                              </h3>
                              <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                                <span>روش پرداخت: {payment.method === 'CreditCard' ? 'کارت اعتباری' : payment.method}</span>
                                <span>•</span>
                                <span>{new Date(payment.createdDate).toLocaleDateString('fa-IR')}</span>
                                {payment.transactionId && (
                                  <>
                                    <span>•</span>
                                    <span>شناسه تراکنش: {payment.transactionId.slice(-8)}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="text-left">
                            <div className="text-xl font-semibold text-gray-900">
                              ${payment.amount.toFixed(2)} {payment.currency}
                            </div>
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                              payment.status === 'Completed' ? 'bg-green-100 text-green-800' :
                              payment.status === 'Failed' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {payment.status === 'Completed' ? 'موفق' :
                               payment.status === 'Failed' ? 'ناموفق' : 'در انتظار'}
                            </span>
                          </div>
                        </div>
                        {payment.failureReason && (
                          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-700">
                              <strong>دلیل عدم موفقیت:</strong> {payment.failureReason}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200">
                    <CreditCardIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">هنوز تراکنشی وجود ندارد</h3>
                    <p className="text-gray-500 mb-6">شما هنوز پرداختی انجام نداده‌اید.</p>
                    <button
                      onClick={() => handleTabSwitch('plans')}
                      className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                      مشاهده پلن‌های موجود
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Plans Tab */}
            {activeTab === 'plans' && (
              <div className="space-y-6 lg:space-y-8">
                {/* Header - Responsive */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <h1 className="text-2xl lg:text-3xl font-semibold text-gray-900">پلن‌های من</h1>
                  <button
                    onClick={() => navigate('/plans')}
                    className="bg-primary-600 hover:bg-primary-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 text-sm sm:text-base"
                  >
                    <DocumentIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                    خرید پلن جدید
                  </button>
                </div>

                {isLoadingUserPlans ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="bg-white rounded-lg p-4 lg:p-6 shadow-sm border border-gray-200 animate-pulse">
                        <div className="space-y-4">
                          <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                          <div className="h-4 bg-gray-200 rounded w-full"></div>
                          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                          <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : userPlans?.data && userPlans.data.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                    {userPlans.data.map((plan: UserPlanDto) => (
                      <div
                        key={plan.id}
                        className={`relative bg-white rounded-xl p-4 lg:p-6 shadow-lg border-2 transition-all duration-300 hover:shadow-xl ${
                          plan.isActive && !plan.isExpired
                            ? `border-${plan.colorClass}-300 bg-gradient-to-br from-${plan.colorClass}-50 to-white` 
                            : plan.isExpired 
                            ? 'border-red-300 bg-gradient-to-br from-red-50 to-white'
                            : 'border-gray-200'
                        }`}
                      >
                        {plan.isActive && !plan.isExpired && (
                          <div className={`absolute -top-2 sm:-top-3 right-3 sm:right-4 bg-gradient-to-r from-${plan.colorClass}-500 to-${plan.colorClass}-600 text-white px-2 sm:px-4 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium shadow-lg`}>
                            فعال
                          </div>
                        )}
                        
                        {plan.isExpired && (
                          <div className="absolute -top-2 sm:-top-3 right-3 sm:right-4 bg-gradient-to-r from-red-500 to-red-600 text-white px-2 sm:px-4 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium shadow-lg">
                            منقضی شده
                          </div>
                        )}
                        
                        {/* Header Section - Mobile Optimized */}
                        <div className="text-center mb-4 lg:mb-6">
                          <div className="text-3xl lg:text-4xl mb-2 lg:mb-3">{plan.iconEmoji}</div>
                          <h3 className="text-lg lg:text-xl font-semibold text-gray-900 mb-1 lg:mb-2">{translatePlanName(plan.name)}</h3>
                          <p className="text-gray-600 text-xs lg:text-sm leading-relaxed line-clamp-2">{translatePlanDescription(plan.description)}</p>
                        </div>

                        {/* Price Section - Mobile Optimized */}
                        <div className="text-center mb-4 lg:mb-6">
                          <div className="text-xl lg:text-2xl font-semibold text-gray-900 mb-1">
                            {plan.price === 0 ? 'رایگان' : `$${plan.price.toFixed(2)}`}
                          </div>
                          <div className="text-xs lg:text-sm text-gray-500">
                            خریداری شده در {new Date(plan.purchaseDate).toLocaleDateString('fa-IR')}
                          </div>
                        </div>

                        {/* Time Remaining - Mobile Optimized */}
                        <div className="mb-4 lg:mb-6">
                          <div className="flex items-center justify-between text-xs lg:text-sm mb-2">
                            <span className="text-gray-600">زمان باقی‌مانده</span>
                            <span className={`font-medium ${plan.isExpired ? 'text-red-600' : plan.remainingDays <= 7 ? 'text-orange-600' : 'text-green-600'}`}>
                              {plan.isExpired ? 'منقضی شده' : `${plan.remainingDays} روز`}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${plan.isExpired ? 'bg-red-500' : plan.remainingDays <= 7 ? 'bg-orange-500' : 'bg-green-500'}`}
                              style={{ width: `${Math.max(0, Math.min(100, (plan.remainingDays / plan.durationDays) * 100))}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            تاریخ انقضا: {new Date(plan.expirationDate).toLocaleDateString('fa-IR')}
                          </div>
                        </div>

                        {/* Ads Count - Mobile Optimized */}
                        <div className="mb-4 lg:mb-6">
                          <div className="flex items-center justify-between text-xs lg:text-sm mb-2">
                            <span className="text-gray-600">تعداد آگهی‌ها</span>
                            <span className={`font-medium ${plan.remainingAdsCount === 0 ? 'text-red-600' : plan.remainingAdsCount <= 2 ? 'text-orange-600' : 'text-green-600'}`}>
                              {plan.remainingAdsCount} از {plan.maxAdsCount}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full ${plan.remainingAdsCount === 0 ? 'bg-red-500' : plan.remainingAdsCount <= 2 ? 'bg-orange-500' : 'bg-green-500'}`}
                              style={{ width: `${Math.max(0, Math.min(100, (plan.remainingAdsCount / plan.maxAdsCount) * 100))}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            استفاده شده: {plan.usedAdsCount} آگهی
                          </div>
                        </div>

                        {/* Plan Ranking Score - Mobile Optimized */}
                        {(plan.rankingBoost || 0) > 0 && (
                          <div className="mb-4 lg:mb-6">
                            <div className="flex items-center justify-between text-xs lg:text-sm mb-2">
                              <span className="text-gray-600">امتیاز رتبه‌بندی پلن</span>
                              {plan.isActive && !plan.isExpired ? (
                                <span className={`font-medium flex items-center gap-1 text-${plan.colorClass}-600`}>
                                  <ChartBarIcon className="h-3 w-3 lg:h-4 lg:w-4" />
                                  {plan.rankingBoost || 0} امتیاز
                                </span>
                              ) : (
                                <span className="font-medium flex items-center gap-1 text-gray-400">
                                  <ChartBarIcon className="h-3 w-3 lg:h-4 lg:w-4" />
                                  غیرفعال
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 bg-blue-50 p-2 rounded">
                              {plan.isActive && !plan.isExpired 
                                ? "آگهی‌های شما به طور خودکار در نتایج جستجو بالاتر نمایش داده می‌شوند"
                                : "این ویژگی برای پلن‌های منقضی شده یا غیرفعال غیرفعال است"
                              }
                            </div>
                          </div>
                        )}

                        {/* Count-based Promotional Features - Mobile Optimized */}
                        <div className="space-y-3 lg:space-y-4 mb-4 lg:mb-6">
                          <h4 className="text-xs lg:text-sm font-medium text-gray-700 border-b border-gray-200 pb-2">ویژگی‌های تبلیغاتی</h4>
                          
                          {/* Top Ad - Top Ad */}
                          {((plan.remainingTopAdCount || 0) > 0 || (plan.usedTopAdCount || 0) > 0) && (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5 lg:gap-2">
                                <div className="text-yellow-500 text-sm lg:text-base">⭐</div>
                                <span className="text-xs lg:text-sm text-gray-700">آگهی برتر</span>
                              </div>
                              <div className="text-xs lg:text-sm">
                                <span className={`font-medium ${(plan.remainingTopAdCount || 0) > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                                  {plan.remainingTopAdCount || 0} از {((plan.remainingTopAdCount || 0) + (plan.usedTopAdCount || 0))}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Highlight - Highlight */}
                          {((plan.remainingHighlightCount || 0) > 0 || (plan.usedHighlightCount || 0) > 0) && (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5 lg:gap-2">
                                <div className="text-orange-500 text-sm lg:text-base">🔥</div>
                                <span className="text-xs lg:text-sm text-gray-700">برجسته</span>
                              </div>
                              <div className="text-xs lg:text-sm">
                                <span className={`font-medium ${(plan.remainingHighlightCount || 0) > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                                  {plan.remainingHighlightCount || 0} از {((plan.remainingHighlightCount || 0) + (plan.usedHighlightCount || 0))}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Urgent Tag - Urgent Tag */}
                          {((plan.remainingUrgentTagCount || 0) > 0 || (plan.usedUrgentTagCount || 0) > 0) && (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5 lg:gap-2">
                                <div className="text-red-500 text-sm lg:text-base">⚡</div>
                                <span className="text-xs lg:text-sm text-gray-700">برچسب فوری</span>
                              </div>
                              <div className="text-xs lg:text-sm">
                                <span className={`font-medium ${(plan.remainingUrgentTagCount || 0) > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                                  {plan.remainingUrgentTagCount || 0} از {((plan.remainingUrgentTagCount || 0) + (plan.usedUrgentTagCount || 0))}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Bump Up - Bump Up */}
                          {((plan.remainingBumpUpCount || 0) > 0 || (plan.usedBumpUpCount || 0) > 0) && (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5 lg:gap-2">
                                <div className="text-purple-500 text-sm lg:text-base">📈</div>
                                <span className="text-xs lg:text-sm text-gray-700">افزایش رتبه</span>
                              </div>
                              <div className="text-xs lg:text-sm">
                                <span className={`font-medium ${(plan.remainingBumpUpCount || 0) > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                                  {plan.remainingBumpUpCount || 0} از {((plan.remainingBumpUpCount || 0) + (plan.usedBumpUpCount || 0))}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Homepage Gallery - Homepage Gallery */}
                          {((plan.remainingHomepageGalleryCount || 0) > 0 || (plan.usedHomepageGalleryCount || 0) > 0) && (
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1.5 lg:gap-2">
                                <div className="text-blue-500 text-sm lg:text-base">🖼️</div>
                                <span className="text-xs lg:text-sm text-gray-700">گالری صفحه اصلی</span>
                              </div>
                              <div className="text-xs lg:text-sm">
                                <span className={`font-medium ${(plan.remainingHomepageGalleryCount || 0) > 0 ? 'text-green-600' : 'text-gray-500'}`}>
                                  {plan.remainingHomepageGalleryCount || 0} از {((plan.remainingHomepageGalleryCount || 0) + (plan.usedHomepageGalleryCount || 0))}
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Show message if no promotional features */}
                          {!(plan.remainingTopAdCount || 0) && !(plan.usedTopAdCount || 0) &&
                           !(plan.remainingHighlightCount || 0) && !(plan.usedHighlightCount || 0) &&
                           !(plan.remainingUrgentTagCount || 0) && !(plan.usedUrgentTagCount || 0) &&
                           !(plan.remainingBumpUpCount || 0) && !(plan.usedBumpUpCount || 0) &&
                           !(plan.remainingHomepageGalleryCount || 0) && !(plan.usedHomepageGalleryCount || 0) && (
                            <div className="text-xs lg:text-sm text-gray-500 text-center py-2">
                              این پلن شامل هیچ ویژگی تبلیغاتی نمی‌شود
                            </div>
                          )}
                        </div>

                        {/* Action Buttons - Mobile Optimized */}
                        <div className="space-y-2">
                          <button
                            onClick={() => {
                              if (plan.isActive && !plan.isExpired) {
                                window.location.href = `/post-ad?planId=${plan.id}`;
                              } else {
                                navigate('/plans');
                              }
                            }}
                            className={`w-full py-2.5 lg:py-3 px-3 lg:px-4 rounded-lg font-medium text-sm lg:text-base transition-all duration-200 ${
                              plan.isActive && !plan.isExpired
                                ? `bg-gradient-to-r from-${plan.colorClass}-500 to-${plan.colorClass}-600 hover:from-${plan.colorClass}-600 hover:to-${plan.colorClass}-700 text-white shadow-lg`
                                : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                            }`}
                          >
                            {plan.isActive && !plan.isExpired ? 'استفاده در آگهی جدید' : plan.isExpired ? 'تمدید پلن' : 'فعال‌سازی'}
                          </button>
                          
                          {plan.isExpired && (
                            <button
                              onClick={() => navigate('/plans')}
                              className="w-full py-2 lg:py-2 px-3 lg:px-4 rounded-lg font-medium text-sm lg:text-base text-primary-600 hover:text-primary-700 border border-primary-300 hover:border-primary-400 transition-colors"
                            >
                              خرید پلن جدید
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : userPlans?.data && userPlans.data.length === 0 ? (
                  <div className="bg-white rounded-xl p-6 lg:p-12 text-center shadow-sm border border-gray-200">
                    <DocumentIcon className="h-12 w-12 lg:h-16 lg:w-16 text-gray-300 mx-auto mb-3 lg:mb-4" />
                    <h3 className="text-base lg:text-lg font-medium text-gray-900 mb-2">هنوز پلنی وجود ندارد</h3>
                    <p className="text-sm lg:text-base text-gray-500 mb-4 lg:mb-6">شما هنوز پلن پریمیومی خریداری نکرده‌اید. ارتقای پلن شما به شما کمک می‌کند تا دید بیشتری داشته باشید و خریداران بیشتری را جذب کنید.</p>
                    <button
                      onClick={() => navigate('/plans')}
                      className="bg-primary-600 hover:bg-primary-700 text-white px-4 lg:px-6 py-2.5 lg:py-3 rounded-lg font-medium text-sm lg:text-base transition-colors"
                    >
                      مشاهده و خرید پلن‌ها
                    </button>
                  </div>
                ) : null}
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="space-y-6 animate-fade-in">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
                    <CogIcon className="h-6 w-6 ml-3 text-primary-600" />
                    تنظیمات حساب
                  </h2>

                  {/* Business Information Section - Only for Business Users */}
                  {businessStatus.userType === 'Business' && (
                    <div className="mb-8 p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                          <BuildingOfficeIcon className="h-5 w-5 ml-2 text-purple-600" />
                          Business Information
                        </h3>
                        {businessStatus.businessApprovalStatus === 1 && (
                          <Link
                            to={`/business/${currentUserData?.data?.businessSlug || 'business'}`}
                            className="text-sm text-purple-600 hover:text-purple-700 flex items-center"
                          >
                            View Business
                            <svg className="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002-2v-2M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </Link>
                        )}
                      </div>

                      {/* Business Approval Status Indicator */}

                      
                      {businessStatus.userType === 'Business' && businessStatus.businessApprovalStatus !== undefined && (
                        <div className="mb-4 p-3 rounded-lg border-r-4 bg-white/50">
                          {(businessStatus.businessApprovalStatus === 0 || businessStatus.businessApprovalStatus === 'Pending') && ( // Pending
                            <div className="border-r-yellow-400">
                              <div className="flex items-center">
                                <div className="flex-shrink-0">
                                  <svg className="h-5 w-5 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <div className="ml-3">
                                  <p className="text-sm font-medium text-yellow-800">
                                    Pending Admin Approval
                                  </p>
                                  <p className="text-xs text-yellow-700 mt-1">
                                    Your changes have been submitted and will be applied after admin review
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          {(businessStatus.businessApprovalStatus === 1 || businessStatus.businessApprovalStatus === 'Approved') && ( // Approved
                            <div className="border-r-green-400">
                              <div className="flex items-center">
                                <div className="flex-shrink-0">
                                  <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <div className="ml-3">
                                  <p className="text-sm font-medium text-green-800">
                                    Business Profile Approved ✅
                                  </p>
                                  <p className="text-xs text-green-700 mt-1">
                                    آخرین تایید: {businessStatus.lastBusinessApprovedAt ? new Date(businessStatus.lastBusinessApprovedAt).toLocaleDateString('fa-IR') : 'نامشخص'}
                                  </p>
                                  <p className="text-xs text-green-600 mt-1">
                                    📧 You should have received an approval confirmation email.
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          {(businessStatus.businessApprovalStatus === 2 || businessStatus.businessApprovalStatus === 'Rejected') && ( // Rejected
                            <div className="border-r-red-400">
                              <div className="flex items-center">
                                <div className="flex-shrink-0">
                                  <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                  </svg>
                                </div>
                                <div className="ml-3">
                                  <p className="text-sm font-medium text-red-800">
                                    Business Profile Rejected
                                  </p>
                                  {businessStatus.businessRejectionReason && (
                                    <p className="text-xs text-red-700 mt-1">
                                      Rejection reason: {businessStatus.businessRejectionReason}
                                    </p>
                                  )}
                                  <p className="text-xs text-red-600 mt-1">
                                    📧 You should have received an email with details about the rejection.
                                  </p>
                                  <button
                                    onClick={async () => {
                                      try {
                                        await businessModerationApi.resubmitProfile();
                                        toast.success('Business profile resubmitted for approval');
                                        // Refresh user data to show updated status
                                        queryClient.invalidateQueries({ queryKey: ['current-user'] });
                                      } catch (error: any) {
                                        toast.error(error.response?.data?.message || 'Failed to resubmit profile');
                                      }
                                    }}
                                    className="mt-2 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                                  >
                                    Resubmit for Approval
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Business Name */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
                          <input
                            type="text"
                            value={businessFormData.businessName || ''}
                            onChange={(e) => handleBusinessFormChange('businessName', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="Enter your business name"
                          />
                        </div>

                        {/* Business Description */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Business Description</label>
                          <textarea
                            rows={4}
                            value={businessFormData.businessDescription || ''}
                            onChange={(e) => handleBusinessFormChange('businessDescription', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="Tell us about your business..."
                          />
                        </div>

                        {/* Business Website */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                          <input
                            type="url"
                            value={businessFormData.businessWebsite || ''}
                            onChange={(e) => handleBusinessFormChange('businessWebsite', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="https://example.com"
                          />
                        </div>

                        {/* Business Address */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Business Address</label>
                          <input
                            type="text"
                            value={businessFormData.businessAddress || ''}
                            onChange={(e) => handleBusinessFormChange('businessAddress', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            placeholder="Complete business address"
                          />
                        </div>

                        {/* Business Hours */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Business Hours</label>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {[
                              { key: 'saturday', label: 'Saturday' },
                              { key: 'sunday', label: 'Sunday' },
                              { key: 'monday', label: 'Monday' },
                              { key: 'tuesday', label: 'Tuesday' },
                              { key: 'wednesday', label: 'Wednesday' },
                              { key: 'thursday', label: 'Thursday' },
                              { key: 'friday', label: 'Friday' }
                            ].map((day) => {
                              const dayData = businessFormData.businessHours?.[day.key as keyof BusinessHours];
                              return (
                                <div key={day.key} className="bg-gray-50 p-3 rounded-lg">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-gray-700">{day.label}</span>
                                    <label className="flex items-center">
                                      <input
                                        type="checkbox"
                                        checked={!dayData?.closed}
                                        onChange={(e) => handleBusinessHoursChange(day.key as keyof BusinessHours, 'closed', !e.target.checked)}
                                        className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                                      />
                                      <span className="ml-2 text-xs text-gray-600">فعال</span>
                                    </label>
                                  </div>
                                  {!dayData?.closed && (
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <label className="block text-xs text-gray-600 mb-1">Open</label>
                                        <input
                                          type="time"
                                          value={dayData?.open || ''}
                                          onChange={(e) => handleBusinessHoursChange(day.key as keyof BusinessHours, 'open', e.target.value)}
                                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                                        />
                                      </div>
                                      <div>
                                        <label className="block text-xs text-gray-600 mb-1">Close</label>
                                        <input
                                          type="time"
                                          value={dayData?.close || ''}
                                          onChange={(e) => handleBusinessHoursChange(day.key as keyof BusinessHours, 'close', e.target.value)}
                                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                                        />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Business Gallery */}
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">Business Gallery</label>
                          <div className="space-y-4">
                            {/* Upload Button */}
                            <div className="flex items-center space-x-3">
                              <button
                                type="button"
                                onClick={() => galleryInputRef.current?.click()}
                                disabled={isUploadingGallery}
                                className="flex items-center px-4 py-2 bg-blue-500 text-white text-sm font-medium rounded-lg hover:bg-blue-600 disabled:opacity-50"
                              >
                                {isUploadingGallery ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-top-transparent mr-2"></div>
                                ) : (
                                  <PhotoIcon className="h-4 w-4 mr-2" />
                                )}
                                {isUploadingGallery ? 'Uploading...' : 'Add Image'}
                              </button>
                              <span className="text-xs text-gray-500">Maximum 10 images</span>
                            </div>
                            
                            <input
                              ref={galleryInputRef}
                              type="file"
                              accept="image/*"
                              multiple
                              onChange={handleGalleryUpload}
                              className="hidden"
                            />

                            {/* Preview Images */}
                            {previewImages.length > 0 && (
                              <div className="space-y-2">
                                <h4 className="text-sm font-medium text-gray-700">Preview of selected images:</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  {previewImages.map((imageUrl, index) => (
                                    <div key={`preview-${index}`} className="relative">
                                      <img
                                        src={imageUrl}
                                        alt={`Preview ${index + 1}`}
                                        className="w-full h-24 object-cover rounded-lg border-2 border-blue-300"
                                      />
                                      <div className="absolute top-1 left-1 bg-blue-500 text-white rounded-full px-2 py-1 text-xs">
                                        New
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Gallery Grid */}
                            {businessGallery.length > 0 && (
                              <div className="space-y-2">
                                <h4 className="text-sm font-medium text-gray-700">Current images:</h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  {businessGallery.map((imageUrl, index) => (
                                    <div key={index} className="relative group">
                                      <img
                                        src={imageUrl}
                                        alt={`Image ${index + 1}`}
                                        className="w-full h-24 object-cover rounded-lg border border-gray-200"
                                      />
                                      <button
                                        onClick={() => handleDeleteGalleryImage(imageUrl)}
                                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {businessGallery.length === 0 && previewImages.length === 0 && (
                              <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                                <PhotoIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                                <p className="text-sm text-gray-500">No images added yet</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 flex justify-end space-x-3">
                        <button
                          type="button"
                          onClick={() => {
                            // Reset form to original data
                            if (currentUserData?.data) {
                              const userData = currentUserData.data;
                              
                              // Parse BusinessHours from JSON string if it exists
                              let parsedBusinessHours;
                              try {
                                parsedBusinessHours = userData.businessHours && typeof userData.businessHours === 'string' 
                                  ? JSON.parse(userData.businessHours) 
                                  : userData.businessHours;
                              } catch (error) {
                                console.error('Error parsing business hours:', error);
                                parsedBusinessHours = null;
                              }
                              
                              // Parse BusinessGallery from new GalleryImages array or fallback to old JSON string
                              let parsedBusinessGallery;
                              try {
                                // Use new GalleryImages array if available
                                if (userData.GalleryImages && Array.isArray(userData.GalleryImages)) {
                                  parsedBusinessGallery = userData.GalleryImages.map((img: any) => img.imageUrl);
                                }
                                // Fallback to old businessGallery JSON string for backward compatibility
                                else if (userData.businessGallery && typeof userData.businessGallery === 'string') {
                                  parsedBusinessGallery = JSON.parse(userData.businessGallery);
                                } else {
                                  parsedBusinessGallery = userData.businessGallery;
                                }
                              } catch (error) {
                                console.error('Error parsing business gallery:', error);
                                parsedBusinessGallery = null;
                              }
                              
                              setBusinessFormData({
                                businessName: userData.businessName || '',
                                businessDescription: userData.businessDescription || '',
                                businessWebsite: userData.businessWebsite || '',
                                businessAddress: userData.businessAddress || '',
                                businessHours: parsedBusinessHours || {
                                  monday: { open: '09:00', close: '18:00', closed: false },
                                  tuesday: { open: '09:00', close: '18:00', closed: false },
                                  wednesday: { open: '09:00', close: '18:00', closed: false },
                                  thursday: { open: '09:00', close: '18:00', closed: false },
                                  friday: { open: '09:00', close: '17:00', closed: false },
                                  saturday: { open: '10:00', close: '16:00', closed: false },
                                  sunday: { open: '', close: '', closed: true }
                                }
                              });
                              setBusinessGallery(parsedBusinessGallery || []);
                            }
                          }}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          لغو
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveBusinessProfile}
                          disabled={isSavingBusiness}
                          className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-500 to-blue-600 rounded-lg hover:from-purple-600 hover:to-blue-700 disabled:opacity-50"
                        >
                          {isSavingBusiness ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-top-transparent inline-block mr-2"></div>
                              در حال ذخیره...
                            </>
                          ) : (
                            'ذخیره تغییرات'
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* General Account Settings */}
                  <div className="space-y-6">
                    <h3 className="text-lg font-semibold text-gray-900">تنظیمات عمومی</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">نام</label>
                        <input
                          type="text"
                          value={generalFormData.firstName}
                          onChange={(e) => setGeneralFormData(prev => ({ ...prev, firstName: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">نام خانوادگی</label>
                        <input
                          type="text"
                          value={generalFormData.lastName}
                          onChange={(e) => setGeneralFormData(prev => ({ ...prev, lastName: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">شماره تلفن</label>
                        <input
                          type="tel"
                          value={generalFormData.phoneNumber}
                          onChange={(e) => setGeneralFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">ایمیل</label>
                        <input
                          type="email"
                          value={currentUserData?.data?.email || currentUser?.email || ''}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50"
                          disabled
                        />
                        <p className="text-xs text-gray-500 mt-1">ایمیل قابل تغییر نیست</p>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                      <button
                        type="button"
                        onClick={() => {
                          // Reset form to original data
                          if (currentUserData?.data) {
                            const userData = currentUserData.data;
                            setGeneralFormData({
                              firstName: userData.firstName || '',
                              lastName: userData.lastName || '',
                              phoneNumber: userData.phoneNumber || ''
                            });
                          }
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        لغو
                      </button>
                      <button
                        type="button"
                        onClick={handleSaveGeneralProfile}
                        disabled={isSavingGeneral}
                        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                      >
                        {isSavingGeneral ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-top-transparent inline-block mr-2"></div>
                            در حال ذخیره...
                          </>
                        ) : (
                          'ذخیره تغییرات'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Other tabs placeholder with enhanced design */}
            {!['profile', 'listings', 'analytics', 'favorites', 'payments', 'plans', 'settings'].includes(activeTab) && (
              <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-200">
                <div className="text-6xl mb-4">🚀</div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                  {sidebarItems.find(item => item.id === activeTab)?.label}
                </h2>
                <p className="text-gray-600 mb-6">این بخش به زودی در دسترس خواهد بود! ما در حال کار روی ویژگی‌های شگفت‌انگیز برای شما هستیم.</p>
                <button
                  onClick={() => handleTabSwitch('profile')}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  بازگشت به پروفایل
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Advanced Filters Modal */}
      {showAdvancedFilters && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" dir="rtl">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">فیلترهای پیشرفته</h2>
                <button
                  onClick={() => setShowAdvancedFilters(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XCircleIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-8">
              {/* Date Range Filter */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">محدوده تاریخ</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">از تاریخ</label>
                    <input
                      type="date"
                      value={advancedFilters.dateRange.start}
                      onChange={(e) => handleAdvancedFilterRangeChange('dateRange', 'start', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">تا تاریخ</label>
                    <input
                      type="date"
                      value={advancedFilters.dateRange.end}
                      onChange={(e) => handleAdvancedFilterRangeChange('dateRange', 'end', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Price Range Filter */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">محدوده قیمت (تومان)</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">حداقل قیمت</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={advancedFilters.priceRange.min}
                      onChange={(e) => handleAdvancedFilterRangeChange('priceRange', 'min', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">حداکثر قیمت</label>
                    <input
                      type="number"
                      placeholder="بدون محدودیت"
                      value={advancedFilters.priceRange.max}
                      onChange={(e) => handleAdvancedFilterRangeChange('priceRange', 'max', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Views Range Filter */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">محدوده بازدیدها</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">حداقل بازدیدها</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={advancedFilters.viewsRange.min}
                      onChange={(e) => handleAdvancedFilterRangeChange('viewsRange', 'min', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">حداکثر بازدیدها</label>
                    <input
                      type="number"
                      placeholder="بدون محدودیت"
                      value={advancedFilters.viewsRange.max}
                      onChange={(e) => handleAdvancedFilterRangeChange('viewsRange', 'max', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Favorites Range Filter */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">محدوده علاقه‌مندی‌ها</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">حداقل علاقه‌مندی</label>
                    <input
                      type="number"
                      placeholder="0"
                      value={advancedFilters.favoritesRange.min}
                      onChange={(e) => handleAdvancedFilterRangeChange('favoritesRange', 'min', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">حداکثر علاقه‌مندی</label>
                    <input
                      type="number"
                      placeholder="بدون محدودیت"
                      value={advancedFilters.favoritesRange.max}
                      onChange={(e) => handleAdvancedFilterRangeChange('favoritesRange', 'max', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Promotional Features Filter */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">ویژگی‌های تبلیغاتی</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <label className="flex items-center space-x-3 space-x-reverse cursor-pointer">
                    <input
                      type="checkbox"
                      checked={advancedFilters.promotionalFeatures.isTopAd}
                      onChange={(e) => handlePromotionalFeatureChange('isTopAd', e.target.checked)}
                      className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">آگهی برتر</span>
                  </label>
                  <label className="flex items-center space-x-3 space-x-reverse cursor-pointer">
                    <input
                      type="checkbox"
                      checked={advancedFilters.promotionalFeatures.isHighlighted}
                      onChange={(e) => handlePromotionalFeatureChange('isHighlighted', e.target.checked)}
                      className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">برجسته</span>
                  </label>
                  <label className="flex items-center space-x-3 space-x-reverse cursor-pointer">
                    <input
                      type="checkbox"
                      checked={advancedFilters.promotionalFeatures.isUrgent}
                      onChange={(e) => handlePromotionalFeatureChange('isUrgent', e.target.checked)}
                      className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">فوری</span>
                  </label>
                  <label className="flex items-center space-x-3 space-x-reverse cursor-pointer">
                    <input
                      type="checkbox"
                      checked={advancedFilters.promotionalFeatures.isFeatured}
                      onChange={(e) => handlePromotionalFeatureChange('isFeatured', e.target.checked)}
                      className="w-4 h-4 text-primary-600 bg-gray-100 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">ویژه</span>
                  </label>
                </div>
              </div>

              {/* Sorting Options */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">مرتب‌سازی</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">مرتب‌سازی بر اساس</label>
                    <select
                      value={advancedFilters.sortBy}
                      onChange={(e) => handleAdvancedFilterChange('sortBy', e.target.value)}
                      className="w-full px-4 py-3 pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-right appearance-none bg-no-repeat"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'left 0.75rem center',
                        backgroundSize: '1.25rem 1.25rem'
                      }}
                    >
                      <option value="datePosted">تاریخ ثبت</option>
                      <option value="price">قیمت</option>
                      <option value="viewCount">تعداد بازدید</option>
                      <option value="favoriteCount">تعداد علاقه‌مندی</option>
                      <option value="title">عنوان</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">ترتیب</label>
                    <select
                      value={advancedFilters.sortOrder}
                      onChange={(e) => handleAdvancedFilterChange('sortOrder', e.target.value)}
                      className="w-full px-4 py-3 pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white text-right appearance-none bg-no-repeat"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
                        backgroundPosition: 'left 0.75rem center',
                        backgroundSize: '1.25rem 1.25rem'
                      }}
                    >
                      <option value="desc">نزولی (جدیدترین اول)</option>
                      <option value="asc">صعودی (قدیمی‌ترین اول)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex items-center justify-between">
                <button
                  onClick={resetAdvancedFilters}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  پاک کردن همه فیلترها
                </button>
                <div className="flex space-x-3 space-x-reverse">
                  <button
                    onClick={() => setShowAdvancedFilters(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    لغو
                  </button>
                  <button
                    onClick={applyAdvancedFilters}
                    className="px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    اعمال فیلترها
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isOpen}
        onClose={cancel}
        onConfirm={confirm}
        title={modalConfig?.title}
        message={modalConfig?.message || ''}
        confirmText={modalConfig?.confirmText}
        cancelText={modalConfig?.cancelText}
        type={modalConfig?.type}
        showCancel={modalConfig?.showCancel}
        isRtl={modalConfig?.isRtl}
      />
    </div>
  );
};

export default UserProfilePage;